/**
 * Elite Batch 14 Part 1 — Workflow Templates 1248–1267
 * Welcome series variants, WhatsApp confirmations, winner ad pipelines,
 * content repurposing, wishlist reminders, and zero-conversion cleanup.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 14 PART 1 — TEMPLATES 1248–1267
// =============================================================================

export const ELITE_BATCH14_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1248. WELCOME WITH ACCOUNT-CREATION PROMPT (8 nodes)
  // ===================================================================

  "Welcome with Account-Creation Prompt": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, looks up CRM profile in HubSpot, branches on existing account status, sends personalized welcome email with account creation CTA, waits 48 hours, checks engagement, and sends reminder email with social proof for non-engagers.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up via website form, popup, or landing page, sending the raw signup payload including email, source, and any custom fields",
        setup:
          "Configure your email platform or form builder to POST subscriber events to this webhook URL. Set up HubSpot API key for CRM lookups. Prepare welcome email templates with account creation CTA.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber data from webhook",
        280,
        "Extract subscriber information from this raw webhook payload. Find the email address, first name, last name, signup source, and any referral or UTM parameters regardless of the field naming format. Return JSON: {email, first_name, last_name, signup_source, utm_source, utm_medium, utm_campaign}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_first_name", type: "string" },
          { field: "last_name", as: "subscriber_last_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_medium", as: "utm_medium", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
        ],
      ),

      // 3. CRM profile lookup HubSpot
      hubspotGetNode(
        "3",
        "Lookup existing HubSpot contact profile",
        520,
        `crm/v3/objects/contacts/{subscriber_email}?idProperty=email&properties=firstname,lastname,lifecyclestage,hs_object_id`,
        [
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.firstname",
            customName: "crm_first_name",
            type: "string",
          },
        ],
      ),

      // 4. Account status branch condition
      n("4", "condition", "Check if subscriber already has an account", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{lifecycle_stage}",
              operator: "equals",
              value: "customer",
            },
          ],
        },
      }),

      // 5. Personalized welcome email (False branch — new subscriber, no account)
      emailNode(
        "5",
        "Send welcome email with account creation CTA",
        1000,
        "{subscriber_email}",
        "Welcome, {subscriber_first_name}! Create your account to unlock exclusive benefits",
        "Hi {subscriber_first_name},\\n\\nWelcome aboard! We are thrilled to have you.\\n\\nYou are just one step away from unlocking your full member experience. Create your free account now to access:\\n\\n- Personalized recommendations\\n- Order tracking and history\\n- Exclusive member-only offers\\n- Early access to new products\\n\\nCreate Your Account: https://{store_domain}/register?email={subscriber_email}&utm_source=welcome_flow&utm_campaign={utm_campaign}\\n\\nJoined from: {signup_source}\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),

      // 5b. Existing customer welcome (True branch)
      emailNode(
        "5b",
        "Send returning customer welcome email",
        1000,
        "{subscriber_email}",
        "Welcome back, {crm_first_name}! Great to see you again",
        "Hi {crm_first_name},\\n\\nWe noticed you re-subscribed — welcome back! As an existing customer, you already have full access to your account.\\n\\nLog in now to see what is new: https://{store_domain}/login?email={subscriber_email}\\n\\nThank you for being a loyal customer!\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 6. Delay 48 hours (from new subscriber path)
      n(
        "6",
        "delayAction",
        "Wait 48 hours for account creation",
        1240,
        {
          delayDetails: { duration: "48", unit: "hours" },
        },
        400,
      ),

      // 7. Engagement check condition
      n(
        "7",
        "condition",
        "Check if account was created within 48hrs",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{lifecycle_stage}",
                operator: "not_equals",
                value: "subscriber",
              },
            ],
          },
        },
        400,
      ),

      // 8. Reminder email with social proof (False — still subscriber)
      emailNode(
        "8",
        "Send reminder email with social proof",
        1720,
        "{subscriber_email}",
        "{subscriber_first_name}, 50,000+ members already love their accounts",
        'Hi {subscriber_first_name},\\n\\nJust a quick reminder — your free account is waiting!\\n\\nHere is what our community is saying:\\n\\n"Creating an account took 30 seconds and the personalized recs are spot on." — Sarah M.\\n"I love being able to track my orders in one place." — James R.\\n"The member-only deals alone make it worth it." — Emily T.\\n\\nJoin 50,000+ happy members: https://{store_domain}/register?email={subscriber_email}&utm_source=welcome_reminder\\n\\nBest,\\nThe {company_name} Team',
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5b", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1249. WELCOME WITH APP-INSTALL PROMPT (9 nodes)
  // ===================================================================

  "Welcome with App-Install Prompt": {
    summary:
      "Receives new user webhook, AI-extracts device and contact data, branches on platform (iOS vs Android), sends platform-specific welcome email with app download CTA, sends SMS app-install nudge, waits 3 days, checks app install status, sends follow-up email to non-installers, and tags segment in HubSpot.",
    nodes: [
      trigger("Webhook: New user registration", "webhook_app", {
        source: "registration_system",
        event: "user.registered",
        howItStarts:
          "Fires when a new user registers on the website or mobile web, sending the raw registration payload including email, phone, device info, and signup context",
        setup:
          "Configure registration system to POST events to this webhook URL. Set up HubSpot API key. Prepare iOS and Android app store links. Configure SMS provider credentials.",
        inputFields: [],
      }),

      // 2. AI extract device data
      aiNode(
        "2",
        "AI extract device and contact data",
        280,
        "Extract user registration data from this raw webhook payload. Find the email, phone number, first name, device type (ios/android/desktop), operating system, browser, and signup page. Return JSON: {email, phone, first_name, device_type, operating_system, browser, signup_page}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "user_email", type: "string" },
          { field: "phone", as: "user_phone", type: "string" },
          { field: "first_name", as: "user_first_name", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "operating_system", as: "operating_system", type: "string" },
          { field: "browser", as: "user_browser", type: "string" },
          { field: "signup_page", as: "signup_page", type: "string" },
        ],
      ),

      // 3. Platform branch condition
      n("3", "condition", "Check if user is on iOS device", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{device_type}", operator: "equals", value: "ios" },
          ],
        },
      }),

      // 4. Welcome email with app CTA (iOS — True branch)
      emailNode(
        "4",
        "Send iOS welcome email with App Store CTA",
        760,
        "{user_email}",
        "Welcome {user_first_name}! Download our iOS app for the best experience",
        "Hi {user_first_name},\\n\\nWelcome to {company_name}! Your account is all set.\\n\\nFor the best experience on your iPhone, download our iOS app:\\n\\nDownload on the App Store: https://apps.apple.com/app/{app_store_id}\\n\\nWith the app you get:\\n- Instant push notifications for deals\\n- Touch ID / Face ID secure login\\n- Offline browsing and wishlist\\n- Exclusive app-only offers\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 4b. Welcome email with app CTA (Android — False branch)
      emailNode(
        "4b",
        "Send Android welcome email with Play Store CTA",
        760,
        "{user_email}",
        "Welcome {user_first_name}! Download our Android app for the best experience",
        "Hi {user_first_name},\\n\\nWelcome to {company_name}! Your account is all set.\\n\\nGet our Android app for the smoothest experience on your device:\\n\\nGet it on Google Play: https://play.google.com/store/apps/details?id={play_store_id}\\n\\nWith the app you get:\\n- Real-time push notifications\\n- Fingerprint secure login\\n- Offline browsing and wishlist\\n- Exclusive app-only offers\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),

      // 5. SMS app-install nudge
      smsNode(
        "5",
        "Send SMS app install nudge",
        1000,
        "{user_phone}",
        "Hey {user_first_name}! Welcome to {company_name}. Download our app for exclusive deals and the best experience: https://{store_domain}/app-download",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for app installation", 1240, {
        delayDetails: { duration: "3", unit: "days" },
      }),

      // 7. App install check condition
      n("7", "condition", "Check if user installed the app", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{lifecycle_stage}",
              operator: "equals",
              value: "app_user",
            },
          ],
        },
      }),

      // 8. Non-installer follow-up email (False — did not install)
      emailNode(
        "8",
        "Send follow-up email to non-installers",
        1720,
        "{user_email}",
        "{user_first_name}, you are missing out on app-exclusive features",
        "Hi {user_first_name},\\n\\nWe noticed you have not downloaded our app yet. Here is what you are missing:\\n\\n- 15% off your first in-app purchase\\n- App-exclusive flash sales\\n- Faster checkout with saved payment methods\\n- Real-time order tracking with push notifications\\n\\nDownload now and use code APPWELCOME for 15% off: https://{store_domain}/app-download\\n\\nThis offer expires in 48 hours!\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),

      // 9. Segment tagging HubSpot (True — installed)
      hubspotPostNode(
        "9",
        "Tag app installer segment in HubSpot",
        1720,
        `/crm/v3/objects/contacts/{hubspot_contact_id}`,
        [
          {
            key: "properties",
            value:
              '{"app_installed":"true","app_install_date":"{_timestamp}","device_platform":"{device_type}","lifecycle_stage":"app_user"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4b", "output_2"),
      e("4", "5"),
      e("4b", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "9", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1250. WELCOME WITH BEST-SELLERS EDUCATION (8 nodes)
  // ===================================================================

  "Welcome with Best-Sellers Education": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, fetches best-seller catalog from Shopify, generates AI-powered product showcase content, sends welcome email with best-seller highlights, waits 3 days, branches on interest category, and sends deep-dive follow-up email.",
    nodes: [
      trigger("Webhook: New email subscriber", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new email subscriber joins via website form, popup, or landing page, sending the raw subscription payload",
        setup:
          "Configure your email capture tool to POST subscriber events to this webhook URL. Set up Shopify API for product catalog access. Configure OpenAI API key for content generation.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber info from webhook",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email address, first name, signup source page URL, and any category or product interest signals. Return JSON: {email, first_name, signup_source, interest_category, referral_url}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "interest_category",
            as: "interest_category",
            type: "string",
          },
          { field: "referral_url", as: "referral_url", type: "string" },
        ],
      ),

      // 3. Shopify best-seller catalog fetch
      shopifyNode(
        "3",
        "Fetch best-selling products from Shopify",
        520,
        "GET",
        "products.json?status=active&published_status=published&limit=10&order=best-selling",
        [],
        [
          {
            name: "products[0].title",
            customName: "best_seller_1_title",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "best_seller_1_price",
            type: "string",
          },
          {
            name: "products[0].images[0].src",
            customName: "best_seller_1_image",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "best_seller_2_title",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "best_seller_2_price",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "best_seller_3_title",
            type: "string",
          },
          {
            name: "products[2].variants[0].price",
            customName: "best_seller_3_price",
            type: "string",
          },
        ],
      ),

      // 4. AI content generation
      aiNode(
        "4",
        "AI generate product showcase content",
        760,
        "You are an e-commerce email copywriter. Create engaging product showcase content for a welcome email featuring best-selling products. Write compelling one-line descriptions for each product and a catchy intro paragraph. Tailor to the subscriber interest category if available. Return JSON: {intro_paragraph, product_1_description, product_2_description, product_3_description, cta_text, subject_line}",
        "Subscriber Name: {subscriber_name}\\nInterest Category: {interest_category}\\nSignup Source: {signup_source}\\n\\nBest Sellers:\\n1. {best_seller_1_title} — ${best_seller_1_price}\\n2. {best_seller_2_title} — ${best_seller_2_price}\\n3. {best_seller_3_title} — ${best_seller_3_price}\\n\\nWrite welcome email content showcasing these products.",
        [
          { field: "intro_paragraph", as: "intro_paragraph", type: "string" },
          {
            field: "product_1_description",
            as: "product_1_desc",
            type: "string",
          },
          {
            field: "product_2_description",
            as: "product_2_desc",
            type: "string",
          },
          {
            field: "product_3_description",
            as: "product_3_desc",
            type: "string",
          },
          { field: "cta_text", as: "cta_text", type: "string" },
          { field: "subject_line", as: "email_subject", type: "string" },
        ],
      ),

      // 5. Welcome email with product showcase
      emailNode(
        "5",
        "Send welcome email with best-seller showcase",
        1000,
        "{subscriber_email}",
        "{email_subject}",
        "Hi {subscriber_name},\\n\\n{intro_paragraph}\\n\\nOur Best Sellers:\\n\\n1. {best_seller_1_title} — ${best_seller_1_price}\\n{product_1_desc}\\n\\n2. {best_seller_2_title} — ${best_seller_2_price}\\n{product_2_desc}\\n\\n3. {best_seller_3_title} — ${best_seller_3_price}\\n{product_3_desc}\\n\\n{cta_text}: https://{store_domain}/collections/best-sellers?utm_source=welcome&utm_campaign=best_sellers\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for engagement signals", 1240, {
        delayDetails: { duration: "3", unit: "days" },
      }),

      // 7. Interest-based branch condition
      n(
        "7",
        "condition",
        "Check if subscriber clicked a product category",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{interest_category}",
                operator: "not_equals",
                value: "",
              },
            ],
          },
        },
      ),

      // 8. Deep-dive follow-up email (True — has interest)
      emailNode(
        "8",
        "Send deep-dive follow-up for interest category",
        1720,
        "{subscriber_email}",
        "{subscriber_name}, more from the {interest_category} collection you loved",
        "Hi {subscriber_name},\\n\\nWe noticed you are interested in our {interest_category} collection!\\n\\nHere is a deeper look at what makes our {interest_category} products special:\\n\\n- Handpicked by our curation team\\n- Highest rated by customers (4.8+ stars average)\\n- Free returns within 30 days\\n\\nExplore the full {interest_category} collection: https://{store_domain}/collections/{interest_category}?utm_source=welcome_followup\\n\\nPlus, use code WELCOME10 for 10% off your first order!\\n\\nBest,\\nThe {company_name} Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1251. WELCOME WITH COMPLIANCE AND CADENCE CHECK (7 nodes)
  // ===================================================================

  "Welcome with Compliance and Cadence Check": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, sends preference center email asking for communication preferences, waits 5 days for response, branches on whether preferences were set, updates CRM preferences in HubSpot, and logs compliance audit trail to Google Sheets.",
    nodes: [
      trigger("Webhook: New subscriber opt-in", "webhook_app", {
        source: "email_platform",
        event: "subscriber.opted_in",
        howItStarts:
          "Fires when a new subscriber completes double opt-in or signs up, sending the raw opt-in payload with email, consent timestamp, and source",
        setup:
          "Configure email platform to POST opt-in events to this webhook. Set up preference center page at your domain. Configure HubSpot API key. Set up Google Sheets for compliance logging.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber and consent data",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, consent timestamp, opt-in source, IP address, and GDPR consent flag regardless of field naming. Return JSON: {email, first_name, consent_timestamp, opt_in_source, ip_address, gdpr_consent}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          {
            field: "consent_timestamp",
            as: "consent_timestamp",
            type: "string",
          },
          { field: "opt_in_source", as: "opt_in_source", type: "string" },
          { field: "ip_address", as: "consent_ip", type: "string" },
          { field: "gdpr_consent", as: "gdpr_consent", type: "string" },
        ],
      ),

      // 3. Preference center email
      emailNode(
        "3",
        "Send preference center welcome email",
        520,
        "{subscriber_email}",
        "Welcome {subscriber_name}! Set your communication preferences",
        "Hi {subscriber_name},\\n\\nWelcome! We are glad you are here.\\n\\nWe respect your inbox and want to send you only what matters most. Please take 30 seconds to set your preferences:\\n\\nSet My Preferences: https://{store_domain}/preferences?email={subscriber_email}&token={preference_token}\\n\\nYou can choose:\\n- Email frequency (daily, weekly, monthly)\\n- Content types (promotions, product news, educational)\\n- Communication channels (email, SMS, push)\\n\\nIf you do not set preferences within 5 days, we will default to our weekly digest.\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 4. Delay 5 days
      n("4", "delayAction", "Wait 5 days for preference response", 760, {
        delayDetails: { duration: "5", unit: "days" },
      }),

      // 5. Preference timeout branch condition
      n("5", "condition", "Check if preferences were submitted", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{gdpr_consent}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 6. CRM preference update HubSpot (True — preferences set)
      hubspotPostNode(
        "6",
        "Update HubSpot contact with preferences",
        1240,
        `/crm/v3/objects/contacts/{subscriber_email}?idProperty=email`,
        [
          {
            key: "properties",
            value:
              '{"email_frequency":"weekly","consent_date":"{consent_timestamp}","consent_ip":"{consent_ip}","opt_in_source":"{opt_in_source}","gdpr_consent":"{gdpr_consent}","preference_status":"confirmed"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 7. Compliance audit log Sheets
      sheetsNode(
        "7",
        "Log compliance audit trail to Sheets",
        1480,
        "ComplianceAudit",
        '[["{subscriber_email}","{consent_timestamp}","{opt_in_source}","{consent_ip}","{gdpr_consent}","preference_confirmed","{_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1252. WELCOME WITH FIRST-PURCHASE COUNTDOWN (9 nodes)
  // ===================================================================

  "Welcome with First-Purchase Countdown": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, initializes countdown timer and discount code via runCode, sends welcome email with urgency, waits 24 hours, sends reminder email, waits another 24 hours, checks purchase status, and sends thank-you email or expired code handler.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up, sending the raw payload with contact information and signup context",
        setup:
          "Configure email platform to POST subscriber events. Set up a unique discount code generation system. Configure Shopify or commerce platform for order tracking.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber data from webhook",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, phone number, and signup source regardless of field names. Return JSON: {email, first_name, phone, signup_source}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "phone", as: "subscriber_phone", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
        ],
      ),

      // 3. Countdown initialization runCode
      n(
        "3",
        "runCode",
        "Generate unique discount code and set expiration",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Generate a unique time-limited discount code
const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
let code = "WELCOME";
for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
const now = new Date();
const expiry = new Date(now.getTime() + 48 * 60 * 60 * 1000);
const hours_remaining = 48;
const discount_percent = 20;
const discount_code = code;
const expiry_date = expiry.toISOString().split("T")[0];
const expiry_time = expiry.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
return { discount_code, discount_percent, hours_remaining, expiry_date, expiry_time };`,
            outputVars: [
              { name: "discount_code", type: "string" },
              { name: "discount_percent", type: "number" },
              { name: "hours_remaining", type: "number" },
              { name: "expiry_date", type: "string" },
              { name: "expiry_time", type: "string" },
            ],
          },
        },
      ),

      // 4. Welcome email with urgency
      emailNode(
        "4",
        "Send welcome email with countdown discount",
        760,
        "{subscriber_email}",
        "{subscriber_name}, your exclusive {discount_percent}% off expires in 48 hours!",
        "Hi {subscriber_name},\\n\\nWelcome to {company_name}! To celebrate, here is an exclusive offer just for you:\\n\\n{discount_percent}% OFF your first purchase\\nCode: {discount_code}\\nExpires: {expiry_date} at {expiry_time}\\n\\nThis code is unique to you and cannot be used by anyone else.\\n\\nShop Now: https://{store_domain}/collections/all?discount={discount_code}&utm_source=welcome_countdown\\n\\nClock is ticking!\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 5. Delay 24hrs
      n("5", "delayAction", "Wait 24 hours before first reminder", 1000, {
        delayDetails: { duration: "24", unit: "hours" },
      }),

      // 6. Reminder email
      emailNode(
        "6",
        "Send 24-hour countdown reminder email",
        1240,
        "{subscriber_email}",
        "Only 24 hours left! Your {discount_percent}% off code is expiring, {subscriber_name}",
        "Hi {subscriber_name},\\n\\nJust a heads up — your exclusive {discount_percent}% off welcome discount expires tomorrow!\\n\\nCode: {discount_code}\\nExpires: {expiry_date} at {expiry_time}\\n\\nDo not miss out on your first purchase savings.\\n\\nShop Now Before It Expires: https://{store_domain}/collections/all?discount={discount_code}&utm_source=welcome_reminder\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 7. Delay 24hrs
      n("7", "delayAction", "Wait final 24 hours", 1480, {
        delayDetails: { duration: "24", unit: "hours" },
      }),

      // 8. Purchase check condition
      n("8", "condition", "Check if subscriber made a purchase", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "{lifecycle_stage}",
              operator: "equals",
              value: "customer",
            },
          ],
        },
      }),

      // 9. Thank-you email (True — purchased)
      emailNode(
        "9",
        "Send first-purchase thank you email",
        1960,
        "{subscriber_email}",
        "Thank you for your first purchase, {subscriber_name}!",
        "Hi {subscriber_name},\\n\\nThank you for your first purchase with us! We are so glad you took advantage of your welcome offer.\\n\\nWhat is next:\\n- Your order confirmation is on its way\\n- You will receive tracking info within 24 hours\\n- Check out our loyalty program for more rewards\\n\\nWelcome to the {company_name} family!\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 9b. Expired code handler (False — did not purchase)
      emailNode(
        "9b",
        "Send expired code follow-up with smaller offer",
        1960,
        "{subscriber_email}",
        "{subscriber_name}, your code expired but we have a little something for you",
        "Hi {subscriber_name},\\n\\nYour welcome code {discount_code} has expired, but we do not want you to miss out entirely.\\n\\nHere is a 10% off code that is good for the next 7 days: WELCOME10\\n\\nShop Now: https://{store_domain}/collections/all?discount=WELCOME10&utm_source=welcome_expired\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
      e("8", "9b", "output_2"),
    ],
  },

  // ===================================================================
  // 1253. WELCOME WITH FIT OR SIZE GUIDE (8 nodes)
  // ===================================================================

  "Welcome with Fit or Size Guide": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, branches on product category (apparel vs footwear vs other), fetches size guide content from Shopify, sends welcome email with embedded size guide, waits 3 days, generates AI-personalized product recommendations, and sends follow-up email with curated picks.",
    nodes: [
      trigger("Webhook: New fashion subscriber", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up on a fashion or apparel store, sending the raw signup payload with email, browsing context, and category interest",
        setup:
          "Configure your store or email capture form to POST signup events. Set up Shopify API access for product catalog. Configure OpenAI API key for personalized recommendations.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber and interest data",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, browsing category (look for apparel, footwear, accessories, or other category signals), gender preference, and size-related hints. Return JSON: {email, first_name, category_interest, gender, size_hint, signup_page}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          {
            field: "category_interest",
            as: "category_interest",
            type: "string",
          },
          { field: "gender", as: "gender_preference", type: "string" },
          { field: "size_hint", as: "size_hint", type: "string" },
          { field: "signup_page", as: "signup_page", type: "string" },
        ],
      ),

      // 3. Category detection branch condition
      n("3", "condition", "Check if subscriber is interested in apparel", 520, {
        conditionDetails: {
          conditions: [
            {
              variable: "{category_interest}",
              operator: "equals",
              value: "apparel",
            },
          ],
        },
      }),

      // 4. Size guide content fetch Shopify (True — apparel)
      shopifyNode(
        "4",
        "Fetch apparel size guide from Shopify",
        760,
        "GET",
        "pages.json?title=size-guide",
        [],
        [
          {
            name: "pages[0].body_html",
            customName: "size_guide_content",
            type: "string",
          },
          {
            name: "pages[0].title",
            customName: "size_guide_title",
            type: "string",
          },
          {
            name: "pages[0].handle",
            customName: "size_guide_handle",
            type: "string",
          },
        ],
      ),

      // 4b. Footwear size guide fetch (False — footwear/other)
      shopifyNode(
        "4b",
        "Fetch footwear fit guide from Shopify",
        760,
        "GET",
        "pages.json?title=fit-guide",
        [],
        [
          {
            name: "pages[0].body_html",
            customName: "size_guide_content",
            type: "string",
          },
          {
            name: "pages[0].title",
            customName: "size_guide_title",
            type: "string",
          },
          {
            name: "pages[0].handle",
            customName: "size_guide_handle",
            type: "string",
          },
        ],
        400,
      ),

      // 5. Welcome email with size guide
      emailNode(
        "5",
        "Send welcome email with size or fit guide",
        1000,
        "{subscriber_email}",
        "Welcome {subscriber_name}! Your personal {size_guide_title} is here",
        "Hi {subscriber_name},\\n\\nWelcome to {company_name}! We want your first purchase to fit perfectly.\\n\\nWe have put together a {size_guide_title} just for you based on your interest in {category_interest}.\\n\\nView Your Guide: https://{store_domain}/pages/{size_guide_handle}?utm_source=welcome_size_guide\\n\\nQuick Tips:\\n- Measure at the widest point\\n- When in between sizes, we recommend sizing up\\n- Our fit experts are available via chat if you need help\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for browsing signals", 1240, {
        delayDetails: { duration: "3", unit: "days" },
      }),

      // 7. AI personalized product recommendations
      aiNode(
        "7",
        "AI generate personalized product picks",
        1480,
        "You are a fashion recommendation engine. Based on the subscriber category interest, gender preference, and size hint, recommend 3 specific product types with descriptions. Make recommendations specific and actionable. Return JSON: {recommendation_1, recommendation_2, recommendation_3, collection_name, style_tip}",
        "Category: {category_interest}\\nGender: {gender_preference}\\nSize Hint: {size_hint}\\nSignup Page: {signup_page}\\n\\nGenerate 3 personalized product recommendations.",
        [
          { field: "recommendation_1", as: "rec_1", type: "string" },
          { field: "recommendation_2", as: "rec_2", type: "string" },
          { field: "recommendation_3", as: "rec_3", type: "string" },
          { field: "collection_name", as: "collection_name", type: "string" },
          { field: "style_tip", as: "style_tip", type: "string" },
        ],
      ),

      // 8. Follow-up email with curated picks
      emailNode(
        "8",
        "Send follow-up email with curated picks",
        1720,
        "{subscriber_email}",
        "{subscriber_name}, we picked these just for you",
        "Hi {subscriber_name},\\n\\nBased on your style preferences, our stylists picked these for you:\\n\\n1. {rec_1}\\n2. {rec_2}\\n3. {rec_3}\\n\\nStyle Tip: {style_tip}\\n\\nShop the {collection_name} Collection: https://{store_domain}/collections/{collection_name}?utm_source=welcome_recs\\n\\nRemember, our size guide is always here to help you find the perfect fit!\\n\\nBest,\\nThe {company_name} Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4b", "output_2"),
      e("4", "5"),
      e("4b", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1254. WELCOME WITH GEO-SPECIFIC STORE OR INVENTORY (9 nodes)
  // ===================================================================

  "Welcome with Geo-Specific Store or Inventory": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, enriches with geolocation API, branches on store availability in region, fetches regional inventory from Shopify, selects geo-targeted promo via runCode, sends personalized welcome email, sends SMS with store directions, and posts error fallback to Slack.",
    nodes: [
      trigger("Webhook: New subscriber with location data", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up, sending the raw payload including email, location data from browser, and signup context",
        setup:
          "Configure email capture to POST subscriber events. Set up ipinfo.io API key for geolocation enrichment. Configure Shopify API for inventory lookups. Set up Slack webhook for error notifications.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber and location data",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, phone number, IP address, city, state/region, country, and postal code regardless of field naming format. Return JSON: {email, first_name, phone, ip_address, city, state, country, postal_code}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "phone", as: "subscriber_phone", type: "string" },
          { field: "ip_address", as: "subscriber_ip", type: "string" },
          { field: "city", as: "subscriber_city", type: "string" },
          { field: "state", as: "subscriber_state", type: "string" },
          { field: "country", as: "subscriber_country", type: "string" },
          { field: "postal_code", as: "subscriber_postal", type: "string" },
        ],
      ),

      // 3. Geolocation enrichment API
      n("3", "hitApi", "Enrich location via ipinfo.io geolocation", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://ipinfo.io/{subscriber_ip}?token={ipinfo_token}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            { name: "city", customName: "geo_city", type: "string" },
            { name: "region", customName: "geo_region", type: "string" },
            { name: "country", customName: "geo_country", type: "string" },
            { name: "loc", customName: "geo_coordinates", type: "string" },
            { name: "postal", customName: "geo_postal", type: "string" },
            { name: "timezone", customName: "geo_timezone", type: "string" },
          ],
        },
      }),

      // 4. Store availability branch condition
      n(
        "4",
        "condition",
        "Check if physical stores exist in subscriber region",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{geo_country}", operator: "equals", value: "US" },
            ],
          },
        },
      ),

      // 5. Regional inventory fetch Shopify (True — US stores available)
      shopifyNode(
        "5",
        "Fetch regional inventory from Shopify",
        1000,
        "GET",
        "locations.json",
        [],
        [
          {
            name: "locations[0].name",
            customName: "nearest_store_name",
            type: "string",
          },
          {
            name: "locations[0].address1",
            customName: "nearest_store_address",
            type: "string",
          },
          {
            name: "locations[0].city",
            customName: "nearest_store_city",
            type: "string",
          },
          {
            name: "locations[0].province",
            customName: "nearest_store_state",
            type: "string",
          },
          {
            name: "locations[0].zip",
            customName: "nearest_store_zip",
            type: "string",
          },
          {
            name: "locations[0].phone",
            customName: "nearest_store_phone",
            type: "string",
          },
        ],
      ),

      // 6. Geo-targeted promo selection runCode
      n("6", "runCode", "Select geo-targeted promo based on region", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select region-specific promotions and shipping options
const region = (vars.geo_region || "").toLowerCase();
const city = (vars.geo_city || "").toLowerCase();
let promo_code = "WELCOME15";
let promo_description = "15% off your first order";
let shipping_offer = "Free standard shipping";
let local_benefit = "Visit us in store for exclusive in-store deals";
// Regional promo tiers
const tier1_regions = ["california","new york","texas","florida"];
const tier2_regions = ["illinois","pennsylvania","ohio","georgia"];
if (tier1_regions.some(r => region.includes(r))) {
  promo_code = "LOCAL20";
  promo_description = "20% off + free same-day delivery";
  shipping_offer = "Free same-day delivery available in your area";
} else if (tier2_regions.some(r => region.includes(r))) {
  promo_code = "REGION18";
  promo_description = "18% off + free 2-day shipping";
  shipping_offer = "Free 2-day shipping to your area";
}
const store_name = vars.nearest_store_name || "our online store";
const store_address = vars.nearest_store_address || "";
return { promo_code, promo_description, shipping_offer, local_benefit, store_name, store_address };`,
          outputVars: [
            { name: "promo_code", type: "string" },
            { name: "promo_description", type: "string" },
            { name: "shipping_offer", type: "string" },
            { name: "local_benefit", type: "string" },
            { name: "store_name", type: "string" },
            { name: "store_address", type: "string" },
          ],
        },
      }),

      // 7. Personalized welcome email
      emailNode(
        "7",
        "Send geo-personalized welcome email",
        1480,
        "{subscriber_email}",
        "Welcome {subscriber_name}! Exclusive offers for {geo_city} shoppers",
        "Hi {subscriber_name},\\n\\nWelcome from {geo_city}, {geo_region}!\\n\\nAs a local shopper, here is what is waiting for you:\\n\\n{promo_description}\\nCode: {promo_code}\\n\\n{shipping_offer}\\n\\nYour Nearest Store: {store_name}\\nAddress: {store_address}\\n\\n{local_benefit}\\n\\nShop Now: https://{store_domain}/collections/all?discount={promo_code}&utm_source=welcome_geo&utm_region={geo_region}\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 8. SMS store directions
      smsNode(
        "8",
        "Send SMS with nearest store directions",
        1720,
        "{subscriber_phone}",
        "Welcome to {company_name}, {subscriber_name}! Your nearest store is {store_name} at {store_address}. Use code {promo_code} for {promo_description}. Get directions: https://maps.google.com/?q={store_address},{nearest_store_city},{nearest_store_state}",
      ),

      // 9. Error fallback Slack (False — no stores in region)
      slackNode(
        "9",
        "Notify team of international subscriber without store",
        1000,
        "International subscriber without local store:\\nEmail: {subscriber_email}\\nLocation: {geo_city}, {geo_region}, {geo_country}\\nTimezone: {geo_timezone}\\nAction: Route to international welcome flow",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "9", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1255. WELCOME WITH GIFT GUIDE (8 nodes)
  // ===================================================================

  "Welcome with Gift Guide": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, classifies interest via runCode, fetches gift guide catalog from Shopify, sends welcome email with curated gift guide, waits 3 days, checks engagement, and sends refined recommendation email for engagers.",
    nodes: [
      trigger("Webhook: New gift-focused subscriber", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up, particularly from gift-related landing pages, sending the raw signup payload",
        setup:
          "Configure email capture to POST subscriber events. Set up Shopify API for gift guide product catalog access. Configure OpenAI API key.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber and gift intent data",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, signup page URL, and any gift-related signals like occasion (birthday, holiday, wedding) or recipient type (him, her, kids, parents). Return JSON: {email, first_name, signup_page, gift_occasion, gift_recipient, budget_range}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "signup_page", as: "signup_page", type: "string" },
          { field: "gift_occasion", as: "gift_occasion", type: "string" },
          { field: "gift_recipient", as: "gift_recipient", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
        ],
      ),

      // 3. Interest classification runCode
      n(
        "3",
        "runCode",
        "Classify gift interest and select guide category",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Classify subscriber into gift guide category
const occasion = (vars.gift_occasion || "general").toLowerCase();
const recipient = (vars.gift_recipient || "anyone").toLowerCase();
const page = (vars.signup_page || "").toLowerCase();
let guide_category = "bestseller-gifts";
let guide_title = "Our Best-Selling Gifts";
let price_filter = "";
// Occasion mapping
if (occasion.includes("birthday")) { guide_category = "birthday-gifts"; guide_title = "Perfect Birthday Gifts"; }
else if (occasion.includes("holiday") || occasion.includes("christmas")) { guide_category = "holiday-gifts"; guide_title = "Holiday Gift Guide"; }
else if (occasion.includes("wedding") || occasion.includes("anniversary")) { guide_category = "luxury-gifts"; guide_title = "Luxury Gift Collection"; }
else if (occasion.includes("baby") || occasion.includes("shower")) { guide_category = "baby-gifts"; guide_title = "Baby & New Parent Gifts"; }
// Budget mapping
const budget = (vars.budget_range || "").toLowerCase();
if (budget.includes("under 25") || budget.includes("<25")) price_filter = "&max_price=25";
else if (budget.includes("under 50") || budget.includes("<50")) price_filter = "&max_price=50";
else if (budget.includes("under 100") || budget.includes("<100")) price_filter = "&max_price=100";
else if (budget.includes("luxury") || budget.includes("100+")) price_filter = "&min_price=100";
return { guide_category, guide_title, price_filter, classified_occasion: occasion, classified_recipient: recipient };`,
            outputVars: [
              { name: "guide_category", type: "string" },
              { name: "guide_title", type: "string" },
              { name: "price_filter", type: "string" },
              { name: "classified_occasion", type: "string" },
              { name: "classified_recipient", type: "string" },
            ],
          },
        },
      ),

      // 4. Gift guide catalog fetch Shopify
      shopifyNode(
        "4",
        "Fetch gift guide products from Shopify",
        760,
        "GET",
        "collections.json?title={guide_category}",
        [],
        [
          {
            name: "collections[0].id",
            customName: "collection_id",
            type: "string",
          },
          {
            name: "collections[0].title",
            customName: "collection_title",
            type: "string",
          },
          {
            name: "collections[0].body_html",
            customName: "collection_description",
            type: "string",
          },
          {
            name: "collections[0].image.src",
            customName: "collection_image",
            type: "string",
          },
          {
            name: "collections[0].products_count",
            customName: "product_count",
            type: "number",
          },
        ],
      ),

      // 5. Welcome email with gift guide
      emailNode(
        "5",
        "Send welcome email with curated gift guide",
        1000,
        "{subscriber_email}",
        "{subscriber_name}, your {guide_title} is ready!",
        "Hi {subscriber_name},\\n\\nWelcome! We see you are looking for the perfect gift. We have curated a special {guide_title} just for you.\\n\\nOccasion: {classified_occasion}\\nFor: {classified_recipient}\\n\\nBrowse {product_count}+ hand-picked items in our {collection_title} collection.\\n\\nView Your Gift Guide: https://{store_domain}/collections/{guide_category}?utm_source=welcome_gift_guide{price_filter}\\n\\nNot sure what to pick? Our gift experts are here to help — just reply to this email!\\n\\nNeed it wrapped? We offer complimentary gift wrapping on all orders.\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for engagement signals", 1240, {
        delayDetails: { duration: "3", unit: "days" },
      }),

      // 7. Engagement tracking condition
      n("7", "condition", "Check if subscriber engaged with gift guide", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{classified_occasion}",
              operator: "not_equals",
              value: "general",
            },
          ],
        },
      }),

      // 8. Refined recommendation email (True — engaged with specific occasion)
      emailNode(
        "8",
        "Send refined gift recommendation email",
        1720,
        "{subscriber_email}",
        "{subscriber_name}, top picks for {classified_occasion} gifts",
        "Hi {subscriber_name},\\n\\nStill searching for the perfect {classified_occasion} gift? Here are our top 3 customer favorites:\\n\\n1. Best for the person who has everything\\n2. Most popular under $50\\n3. Editor's choice for {classified_recipient}\\n\\nShop Top Picks: https://{store_domain}/collections/{guide_category}?sort_by=best-selling&utm_source=welcome_gift_followup{price_filter}\\n\\nRemember: Free shipping on orders over $50 and complimentary gift wrapping included!\\n\\nBest,\\nThe {company_name} Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1256. WELCOME WITH LOYALTY-ENROLLMENT CTA (8 nodes)
  // ===================================================================

  "Welcome with Loyalty-Enrollment CTA": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, checks existing loyalty membership in HubSpot, branches on enrollment status, sends welcome email with loyalty CTA for non-members, waits 5 days, sends non-enrollment follow-up email, and tags CRM segment in HubSpot.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up via any channel, sending the raw payload with contact information",
        setup:
          "Configure email platform to POST subscriber events. Set up HubSpot API key for loyalty membership lookups. Prepare loyalty program enrollment page.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber data from webhook",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email address, first name, last name, and any loyalty or rewards program references. Return JSON: {email, first_name, last_name, loyalty_reference}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "last_name", as: "subscriber_last_name", type: "string" },
          {
            field: "loyalty_reference",
            as: "loyalty_reference",
            type: "string",
          },
        ],
      ),

      // 3. Existing loyalty check HubSpot
      hubspotGetNode(
        "3",
        "Check existing loyalty membership in HubSpot",
        520,
        `crm/v3/objects/contacts/{subscriber_email}?idProperty=email&properties=loyalty_member,loyalty_tier,loyalty_points,hs_object_id`,
        [
          {
            name: "properties.loyalty_member",
            customName: "is_loyalty_member",
            type: "string",
          },
          {
            name: "properties.loyalty_tier",
            customName: "loyalty_tier",
            type: "string",
          },
          {
            name: "properties.loyalty_points",
            customName: "loyalty_points",
            type: "string",
          },
          { name: "id", customName: "hubspot_contact_id", type: "string" },
        ],
      ),

      // 4. Enrollment status branch condition
      n("4", "condition", "Check if already a loyalty member", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{is_loyalty_member}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 5a. Welcome email for existing loyalty members (True)
      emailNode(
        "5a",
        "Send loyalty member welcome email",
        1000,
        "{subscriber_email}",
        "Welcome back, {subscriber_name}! Your {loyalty_tier} perks await",
        "Hi {subscriber_name},\\n\\nWelcome! As a {loyalty_tier} loyalty member with {loyalty_points} points, you already have access to exclusive perks.\\n\\nYour Current Benefits:\\n- {loyalty_tier} tier pricing on all items\\n- Points multiplier on every purchase\\n- Early access to sales and new arrivals\\n\\nCheck Your Points Balance: https://{store_domain}/account/loyalty?utm_source=welcome_loyalty_member\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 5b. Welcome email with loyalty CTA for non-members (False)
      emailNode(
        "5b",
        "Send welcome email with loyalty enrollment CTA",
        1000,
        "{subscriber_email}",
        "Welcome {subscriber_name}! Join our loyalty program and earn rewards from day one",
        "Hi {subscriber_name},\\n\\nWelcome to {company_name}!\\n\\nDid you know we have a loyalty program? Join for free and start earning rewards immediately:\\n\\n- Earn 1 point for every $1 spent\\n- 500 bonus points just for signing up\\n- Exclusive member-only sales and early access\\n- Birthday rewards and tier upgrades\\n\\nJoin the Loyalty Program: https://{store_domain}/loyalty/enroll?email={subscriber_email}&utm_source=welcome_loyalty_cta\\n\\nIt takes less than 30 seconds and the 500 bonus points are yours instantly!\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),

      // 6. Delay 5 days (from non-member path)
      n(
        "6",
        "delayAction",
        "Wait 5 days for loyalty enrollment",
        1240,
        {
          delayDetails: { duration: "5", unit: "days" },
        },
        400,
      ),

      // 7. Non-enrollment follow-up email
      emailNode(
        "7",
        "Send loyalty enrollment follow-up email",
        1480,
        "{subscriber_email}",
        "{subscriber_name}, your 500 bonus points are still waiting",
        "Hi {subscriber_name},\\n\\nYour 500 bonus loyalty points are still unclaimed!\\n\\nHere is a peek at what members are enjoying:\\n\\n- Free shipping on all orders (Silver tier and above)\\n- 2x points on weekends\\n- Exclusive access to limited-edition products\\n- Annual birthday gift worth up to $50\\n\\nClaim Your 500 Points Now: https://{store_domain}/loyalty/enroll?email={subscriber_email}&utm_source=welcome_loyalty_reminder\\n\\nDo not miss out — this bonus offer is available for new subscribers only!\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),

      // 8. CRM tagging HubSpot
      hubspotPostNode(
        "8",
        "Tag loyalty prospect segment in HubSpot",
        1720,
        `/crm/v3/objects/contacts/{hubspot_contact_id}`,
        [
          {
            key: "properties",
            value:
              '{"loyalty_prospect":"true","loyalty_welcome_sent":"true","loyalty_followup_sent":"true","welcome_flow_completed":"{_timestamp}"}',
          },
        ],
        [{ name: "id", customName: "tagged_contact_id", type: "string" }],
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5a", "output_1"),
      e("4", "5b", "output_2"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1257. WELCOME WITH PROFILE-COMPLETION PROMPT (8 nodes)
  // ===================================================================

  "Welcome with Profile-Completion Prompt": {
    summary:
      "Receives new user webhook, AI-extracts user data, assesses profile completeness via runCode, sends welcome email with profile completion CTA, waits 5 days, branches on completion milestone, sends incentive unlock email for complete profiles, and activates recommendation engine in HubSpot.",
    nodes: [
      trigger("Webhook: New user account created", "webhook_app", {
        source: "user_system",
        event: "user.created",
        howItStarts:
          "Fires when a new user account is created, sending the raw payload with whatever profile fields were filled at registration",
        setup:
          "Configure user registration system to POST events. Set up profile completion tracking. Configure HubSpot API key for contact updates. Prepare profile completion landing page.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract user profile data from webhook",
        280,
        "Extract user profile data from this raw webhook payload. Find the email, first name, last name, phone, avatar status, bio, preferences, and any other profile fields. Return JSON: {email, first_name, last_name, phone, has_avatar, has_bio, has_preferences, profile_fields_count}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "user_email", type: "string" },
          { field: "first_name", as: "user_first_name", type: "string" },
          { field: "last_name", as: "user_last_name", type: "string" },
          { field: "phone", as: "user_phone", type: "string" },
          { field: "has_avatar", as: "has_avatar", type: "string" },
          { field: "has_bio", as: "has_bio", type: "string" },
          { field: "has_preferences", as: "has_preferences", type: "string" },
          {
            field: "profile_fields_count",
            as: "profile_fields_count",
            type: "number",
          },
        ],
      ),

      // 3. Profile completeness assessment runCode
      n("3", "runCode", "Calculate profile completeness score", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate profile completeness percentage
const fields = {
  first_name: vars.user_first_name && vars.user_first_name !== "" ? 1 : 0,
  last_name: vars.user_last_name && vars.user_last_name !== "" ? 1 : 0,
  phone: vars.user_phone && vars.user_phone !== "" ? 1 : 0,
  avatar: vars.has_avatar === "true" ? 1 : 0,
  bio: vars.has_bio === "true" ? 1 : 0,
  preferences: vars.has_preferences === "true" ? 1 : 0,
};
const completed = Object.values(fields).reduce((a, b) => a + b, 0);
const total = Object.keys(fields).length;
const completeness_percent = Math.round((completed / total) * 100);
const missing_fields = Object.entries(fields).filter(([k, v]) => v === 0).map(([k]) => k).join(", ");
const is_complete = completeness_percent >= 80 ? "yes" : "no";
const next_step = missing_fields.split(", ")[0] || "all done";
return { completeness_percent, missing_fields, is_complete, next_step, completed_count: completed, total_fields: total };`,
          outputVars: [
            { name: "completeness_percent", type: "number" },
            { name: "missing_fields", type: "string" },
            { name: "is_complete", type: "string" },
            { name: "next_step", type: "string" },
            { name: "completed_count", type: "number" },
            { name: "total_fields", type: "number" },
          ],
        },
      }),

      // 4. Welcome email with profile CTA
      emailNode(
        "4",
        "Send welcome email with profile completion CTA",
        760,
        "{user_email}",
        "Welcome {user_first_name}! Complete your profile to unlock rewards",
        "Hi {user_first_name},\\n\\nWelcome to {company_name}! Your account is set up and ready.\\n\\nYour profile is {completeness_percent}% complete ({completed_count}/{total_fields} fields).\\n\\nComplete your profile to unlock:\\n- Personalized product recommendations\\n- Faster checkout\\n- Exclusive member rewards\\n- Birthday surprises\\n\\nNext step: Add your {next_step}\\n\\nComplete Your Profile: https://{store_domain}/account/profile?utm_source=welcome_profile\\n\\nMissing: {missing_fields}\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 5. Delay 5 days
      n("5", "delayAction", "Wait 5 days for profile completion", 1000, {
        delayDetails: { duration: "5", unit: "days" },
      }),

      // 6. Completion milestone branch condition
      n("6", "condition", "Check if profile is at least 80% complete", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{is_complete}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 7. Incentive unlock email (True — profile complete)
      emailNode(
        "7",
        "Send incentive unlock email for complete profile",
        1480,
        "{user_email}",
        "Congrats {user_first_name}! Your profile is complete — here is your reward",
        "Hi {user_first_name},\\n\\nAmazing! Your profile is now {completeness_percent}% complete.\\n\\nAs a thank you, here is your exclusive reward:\\n\\nFree Shipping on Your Next Order\\nCode: PROFILEPERK\\n\\nPlus, your personalized recommendations are now active! We will start showing you products tailored to your preferences.\\n\\nStart Shopping: https://{store_domain}/for-you?utm_source=welcome_profile_complete\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 8. Recommendation engine activation HubSpot
      hubspotPostNode(
        "8",
        "Activate recommendation engine in HubSpot",
        1720,
        `/crm/v3/objects/contacts/{user_email}?idProperty=email`,
        [
          {
            key: "properties",
            value:
              '{"profile_complete":"true","profile_completeness":"{completeness_percent}","recommendations_active":"true","profile_completed_date":"{_timestamp}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1258. WELCOME WITH STORE LOCATOR (7 nodes)
  // ===================================================================

  "Welcome with Store Locator": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, geocodes address and searches for nearby stores via API, branches on store proximity, sends welcome email with store card and directions, waits 5 days, and branches on visit follow-up status.",
    nodes: [
      trigger("Webhook: New subscriber with address", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up with location information, sending the raw payload including email, address, and signup context",
        setup:
          "Configure email capture to POST subscriber events. Set up Google Maps Geocoding API key for address lookup. Prepare store locator data.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber and address data",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, street address, city, state, zip code, and country. Return JSON: {email, first_name, address, city, state, zip_code, country}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "address", as: "subscriber_address", type: "string" },
          { field: "city", as: "subscriber_city", type: "string" },
          { field: "state", as: "subscriber_state", type: "string" },
          { field: "zip_code", as: "subscriber_zip", type: "string" },
          { field: "country", as: "subscriber_country", type: "string" },
        ],
      ),

      // 3. Geocoding and store search API
      n("3", "hitApi", "Geocode address and find nearby stores", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://maps.googleapis.com/maps/api/geocode/json?address={subscriber_address},{subscriber_city},{subscriber_state},{subscriber_zip}&key={google_maps_key}",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "results[0].geometry.location.lat",
              customName: "subscriber_lat",
              type: "string",
            },
            {
              name: "results[0].geometry.location.lng",
              customName: "subscriber_lng",
              type: "string",
            },
            {
              name: "results[0].formatted_address",
              customName: "formatted_address",
              type: "string",
            },
            { name: "status", customName: "geocode_status", type: "string" },
          ],
        },
      }),

      // 4. Store proximity branch condition
      n("4", "condition", "Check if geocoding returned valid results", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{geocode_status}", operator: "equals", value: "OK" },
          ],
        },
      }),

      // 5. Welcome email with store card (True — valid geocode)
      emailNode(
        "5",
        "Send welcome email with nearest store info",
        1000,
        "{subscriber_email}",
        "Welcome {subscriber_name}! Find your nearest {company_name} store",
        "Hi {subscriber_name},\\n\\nWelcome to {company_name}!\\n\\nWe found stores near {formatted_address}:\\n\\nYour Nearest Store:\\nAddress: Check our store locator for the closest location\\nGet Directions: https://www.google.com/maps/search/{company_name}+near+{subscriber_lat},{subscriber_lng}\\n\\nWhy Visit In Store?\\n- Try before you buy\\n- Exclusive in-store promotions\\n- Same-day pickup available\\n- Personal shopping assistance\\n\\nFind All Stores: https://{store_domain}/store-locator?lat={subscriber_lat}&lng={subscriber_lng}&utm_source=welcome_store\\n\\nCan not visit? Shop online with code WELCOME15 for 15% off!\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 6. Delay 5 days
      n("6", "delayAction", "Wait 5 days for store visit", 1240, {
        delayDetails: { duration: "5", unit: "days" },
      }),

      // 7. Visit follow-up branch condition
      n("7", "condition", "Check if subscriber visited a store", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{lifecycle_stage}",
              operator: "equals",
              value: "store_visitor",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1259. WELCOME WITH SUBSCRIPTION-ENROLLMENT CTA (8 nodes)
  // ===================================================================

  "Welcome with Subscription-Enrollment CTA": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, checks purchase history in HubSpot, branches on customer status, sends welcome email with subscription CTA, waits 5 days, sends follow-up comparison email, and sends non-enrollment nurture email for those who still have not subscribed.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up, sending the raw payload with contact information and shopping context",
        setup:
          "Configure email capture to POST subscriber events. Set up HubSpot API for purchase history lookups. Prepare subscription product comparison page.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber data from webhook",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, and any product interest or subscription inquiry signals. Return JSON: {email, first_name, product_interest, subscription_inquiry}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "product_interest", as: "product_interest", type: "string" },
          {
            field: "subscription_inquiry",
            as: "subscription_inquiry",
            type: "string",
          },
        ],
      ),

      // 3. Purchase history check HubSpot
      hubspotGetNode(
        "3",
        "Check purchase and subscription history in HubSpot",
        520,
        `crm/v3/objects/contacts/{subscriber_email}?idProperty=email&properties=num_associated_deals,subscription_status,total_revenue,last_purchase_date,hs_object_id`,
        [
          {
            name: "properties.num_associated_deals",
            customName: "total_purchases",
            type: "string",
          },
          {
            name: "properties.subscription_status",
            customName: "subscription_status",
            type: "string",
          },
          {
            name: "properties.total_revenue",
            customName: "total_revenue",
            type: "string",
          },
          {
            name: "properties.last_purchase_date",
            customName: "last_purchase_date",
            type: "string",
          },
          { name: "id", customName: "hubspot_contact_id", type: "string" },
        ],
      ),

      // 4. Customer status branch condition
      n("4", "condition", "Check if subscriber is already a subscriber", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{subscription_status}",
              operator: "equals",
              value: "active",
            },
          ],
        },
      }),

      // 5a. Existing subscriber welcome (True)
      emailNode(
        "5a",
        "Send active subscriber welcome email",
        1000,
        "{subscriber_email}",
        "Welcome back, {subscriber_name}! Your subscription benefits",
        "Hi {subscriber_name},\\n\\nWelcome! Great news — you are already an active subscriber.\\n\\nYour Subscription Perks:\\n- Free shipping on every delivery\\n- 15% off all subscription orders\\n- Flexible scheduling — skip, pause, or swap anytime\\n- Priority customer support\\n\\nManage Your Subscription: https://{store_domain}/account/subscriptions?utm_source=welcome_subscriber\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 5b. Welcome email with subscription CTA (False — not yet subscriber)
      emailNode(
        "5b",
        "Send welcome email with subscription enrollment CTA",
        1000,
        "{subscriber_email}",
        "Welcome {subscriber_name}! Save 15% with a subscription — never run out again",
        "Hi {subscriber_name},\\n\\nWelcome to {company_name}!\\n\\nDid you know you can save 15% and never worry about running out of your favorites?\\n\\nSubscription Benefits:\\n- 15% off every order, automatically\\n- Free shipping on all deliveries\\n- Flexible — change, skip, or cancel anytime\\n- Choose your delivery schedule (weekly, bi-weekly, monthly)\\n\\nInterested in: {product_interest}\\n\\nStart Your Subscription: https://{store_domain}/subscribe?product={product_interest}&utm_source=welcome_subscription_cta\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),

      // 6. Delay 5 days
      n(
        "6",
        "delayAction",
        "Wait 5 days for subscription enrollment",
        1240,
        {
          delayDetails: { duration: "5", unit: "days" },
        },
        400,
      ),

      // 7. Follow-up comparison email
      emailNode(
        "7",
        "Send subscription comparison follow-up email",
        1480,
        "{subscriber_email}",
        "{subscriber_name}, see how much you could save with Subscribe & Save",
        "Hi {subscriber_name},\\n\\nHere is a quick comparison of one-time vs subscription pricing:\\n\\nOne-Time Purchase:\\n- Full price on every order\\n- Standard shipping rates\\n- Manual reordering\\n\\nSubscribe & Save:\\n- 15% off every order\\n- Free shipping always\\n- Automatic delivery on your schedule\\n- Cancel or modify anytime\\n\\nExample: If you spend $50/month, that is $90 saved per year!\\n\\nCompare Plans: https://{store_domain}/subscribe/compare?utm_source=welcome_subscription_compare\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),

      // 8. Non-enrollment nurture email
      emailNode(
        "8",
        "Send final subscription nurture email",
        1720,
        "{subscriber_email}",
        "Last chance: {subscriber_name}, get an extra 5% off your first subscription order",
        "Hi {subscriber_name},\\n\\nWe have saved the best for last!\\n\\nFor a limited time, get an extra 5% off your first subscription order — that is 20% total savings!\\n\\nUse code: SUBSCRIBE20\\n\\nThis offer expires in 72 hours.\\n\\nStart Saving: https://{store_domain}/subscribe?discount=SUBSCRIBE20&utm_source=welcome_subscription_final\\n\\nBest,\\nThe {company_name} Team",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5a", "output_1"),
      e("4", "5b", "output_2"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1260. WELCOME WITH UGC AND SOCIAL PROOF (8 nodes)
  // ===================================================================

  "Welcome with UGC and Social Proof": {
    summary:
      "Receives new subscriber webhook, AI-extracts subscriber data, fetches UGC content via API, aggregates review stats via runCode, sends welcome email with UGC showcase, waits 3 days, sends category-specific follow-up email, and logs social channel cross-promotion data to HubSpot.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up, sending the raw payload with email, name, and signup context",
        setup:
          "Configure email platform to POST subscriber events. Set up review platform API (Yotpo, Judge.me, or Stamped) for UGC fetching. Configure HubSpot API key.",
        inputFields: [],
      }),

      // 2. AI extract subscriber data
      aiNode(
        "2",
        "AI extract subscriber data from webhook",
        280,
        "Extract subscriber data from this raw webhook payload. Find the email, first name, signup source, and any product category interest. Return JSON: {email, first_name, signup_source, category_interest}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "email", as: "subscriber_email", type: "string" },
          { field: "first_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "category_interest",
            as: "category_interest",
            type: "string",
          },
        ],
      ),

      // 3. UGC content fetch API
      n("3", "hitApi", "Fetch top customer reviews and UGC content", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yotpo.com/v1/apps/{yotpo_app_key}/reviews?per_page=5&sort=rating&direction=desc",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "reviews[0].content",
              customName: "review_1_content",
              type: "string",
            },
            {
              name: "reviews[0].score",
              customName: "review_1_score",
              type: "number",
            },
            {
              name: "reviews[0].user.display_name",
              customName: "review_1_author",
              type: "string",
            },
            {
              name: "reviews[1].content",
              customName: "review_2_content",
              type: "string",
            },
            {
              name: "reviews[1].score",
              customName: "review_2_score",
              type: "number",
            },
            {
              name: "reviews[1].user.display_name",
              customName: "review_2_author",
              type: "string",
            },
            {
              name: "reviews[2].content",
              customName: "review_3_content",
              type: "string",
            },
            {
              name: "reviews[2].user.display_name",
              customName: "review_3_author",
              type: "string",
            },
            {
              name: "pagination.total",
              customName: "total_reviews",
              type: "number",
            },
          ],
        },
      }),

      // 4. Review stats aggregation runCode
      n("4", "runCode", "Aggregate review stats for social proof", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate review statistics for social proof
const totalReviews = parseInt(vars.total_reviews) || 0;
const r1Score = parseInt(vars.review_1_score) || 5;
const r2Score = parseInt(vars.review_2_score) || 5;
const avgScore = ((r1Score + r2Score) / 2).toFixed(1);
const reviewCount = totalReviews.toLocaleString();
// Truncate reviews to first 150 chars for email
const r1 = (vars.review_1_content || "").substring(0, 150);
const r2 = (vars.review_2_content || "").substring(0, 150);
const r3 = (vars.review_3_content || "").substring(0, 150);
const star_display = "★".repeat(Math.round(parseFloat(avgScore))) + "☆".repeat(5 - Math.round(parseFloat(avgScore)));
return { avg_rating: avgScore, review_count: reviewCount, review_1_short: r1, review_2_short: r2, review_3_short: r3, star_display };`,
          outputVars: [
            { name: "avg_rating", type: "string" },
            { name: "review_count", type: "string" },
            { name: "review_1_short", type: "string" },
            { name: "review_2_short", type: "string" },
            { name: "review_3_short", type: "string" },
            { name: "star_display", type: "string" },
          ],
        },
      }),

      // 5. Welcome email with UGC showcase
      emailNode(
        "5",
        "Send welcome email with UGC and social proof",
        1000,
        "{subscriber_email}",
        "Welcome {subscriber_name}! See why {review_count} customers love us",
        'Hi {subscriber_name},\\n\\nWelcome to {company_name}! Do not just take our word for it — here is what our community says:\\n\\n{star_display} {avg_rating}/5 from {review_count} reviews\\n\\n"{review_1_short}..."\\n— {review_1_author}\\n\\n"{review_2_short}..."\\n— {review_2_author}\\n\\n"{review_3_short}..."\\n— {review_3_author}\\n\\nSee All Reviews: https://{store_domain}/reviews?utm_source=welcome_ugc\\n\\nShop Our Most Loved Products: https://{store_domain}/collections/top-rated?utm_source=welcome_ugc\\n\\nBest,\\nThe {company_name} Team',
      ),

      // 6. Delay 3 days
      n("6", "delayAction", "Wait 3 days for engagement signals", 1240, {
        delayDetails: { duration: "3", unit: "days" },
      }),

      // 7. Category-specific follow-up email
      emailNode(
        "7",
        "Send category-specific follow-up with reviews",
        1480,
        "{subscriber_email}",
        "{subscriber_name}, top-rated {category_interest} picks our customers swear by",
        "Hi {subscriber_name},\\n\\nSince you showed interest in {category_interest}, here are the top-rated picks in that category:\\n\\nAll verified by real customers with {avg_rating}+ star ratings.\\n\\nBrowse Top-Rated {category_interest}: https://{store_domain}/collections/{category_interest}?sort_by=rating&utm_source=welcome_ugc_followup\\n\\nJoin our community on social media to see even more customer photos and stories!\\n\\nInstagram: @{company_name}\\nTikTok: @{company_name}\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 8. Social channel cross-promotion HubSpot
      hubspotPostNode(
        "8",
        "Log social proof engagement in HubSpot",
        1720,
        `/crm/v3/objects/contacts/{subscriber_email}?idProperty=email`,
        [
          {
            key: "properties",
            value:
              '{"ugc_welcome_sent":"true","category_interest":"{category_interest}","social_proof_score":"{avg_rating}","welcome_ugc_flow_completed":"{_timestamp}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1261. WHATSAPP OPT-IN CONFIRMATION (7 nodes)
  // ===================================================================

  "WhatsApp Opt-In Confirmation": {
    summary:
      "Receives opt-in webhook, AI-extracts phone and contact data, validates consent via runCode, sends email confirmation, sends SMS confirmation, sends WhatsApp welcome message via API, and logs consent to HubSpot CRM.",
    nodes: [
      trigger("Webhook: WhatsApp opt-in request", "webhook_app", {
        source: "whatsapp_optin",
        event: "optin.submitted",
        howItStarts:
          "Fires when a user submits a WhatsApp opt-in form on the website or during checkout, sending the raw form payload with phone number and consent details",
        setup:
          "Configure opt-in form to POST to this webhook. Set up WhatsApp Business API (Meta Cloud API) credentials. Configure HubSpot API key for consent logging.",
        inputFields: [],
      }),

      // 2. AI extract phone data
      aiNode(
        "2",
        "AI extract phone and contact data",
        280,
        "Extract contact data from this raw webhook payload. Find the phone number (with country code), email address, first name, consent text they agreed to, and the timestamp of opt-in. Return JSON: {phone, email, first_name, consent_text, optin_timestamp, country_code}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "phone", as: "user_phone", type: "string" },
          { field: "email", as: "user_email", type: "string" },
          { field: "first_name", as: "user_name", type: "string" },
          { field: "consent_text", as: "consent_text", type: "string" },
          { field: "optin_timestamp", as: "optin_timestamp", type: "string" },
          { field: "country_code", as: "country_code", type: "string" },
        ],
      ),

      // 3. Consent validation runCode
      n("3", "runCode", "Validate consent data and format phone number", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate consent and normalize phone number
const phone = (vars.user_phone || "").replace(/[^0-9+]/g, "");
const email = (vars.user_email || "").trim().toLowerCase();
const issues = [];
// Phone validation
if (phone.length < 10) issues.push("Phone number too short");
if (!phone.startsWith("+") && phone.length < 11) issues.push("Missing country code");
// Consent validation
if (!vars.consent_text || vars.consent_text.length < 10) issues.push("Consent text missing or insufficient");
if (!vars.optin_timestamp) issues.push("Opt-in timestamp missing");
// Format phone for WhatsApp API (remove + prefix)
const wa_phone = phone.startsWith("+") ? phone.substring(1) : phone;
const consent_valid = issues.length === 0 ? "yes" : "no";
const validation_issues = issues.length > 0 ? issues.join("; ") : "All validations passed";
return { wa_phone, consent_valid, validation_issues, normalized_email: email };`,
          outputVars: [
            { name: "wa_phone", type: "string" },
            { name: "consent_valid", type: "string" },
            { name: "validation_issues", type: "string" },
            { name: "normalized_email", type: "string" },
          ],
        },
      }),

      // 4. Email confirmation
      emailNode(
        "4",
        "Send email confirming WhatsApp opt-in",
        760,
        "{normalized_email}",
        "Confirmed: You are now subscribed to {company_name} on WhatsApp",
        "Hi {user_name},\\n\\nThis confirms that you have opted in to receive messages from {company_name} on WhatsApp.\\n\\nPhone Number: {user_phone}\\nOpt-In Date: {optin_timestamp}\\nConsent: {consent_text}\\n\\nWhat to expect:\\n- Order updates and shipping notifications\\n- Exclusive deals and flash sale alerts\\n- Customer support via WhatsApp\\n\\nYou can opt out at any time by replying STOP to any WhatsApp message.\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 5. SMS confirmation
      smsNode(
        "5",
        "Send SMS confirming WhatsApp subscription",
        1000,
        "{user_phone}",
        "{company_name}: You are now subscribed to our WhatsApp updates! You will receive your first message shortly. Reply STOP to unsubscribe at any time.",
      ),

      // 6. WhatsApp welcome message API
      n("6", "hitApi", "Send WhatsApp welcome message via Meta API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{whatsapp_phone_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "messaging_product", value: "whatsapp" },
            { key: "to", value: "{wa_phone}" },
            { key: "type", value: "template" },
            {
              key: "template",
              value:
                '{"name":"welcome_optin","language":{"code":"en"},"components":[{"type":"body","parameters":[{"type":"text","text":"{user_name}"}]}]}',
            },
          ],
          variables: [
            {
              name: "messages[0].id",
              customName: "wa_message_id",
              type: "string",
            },
            {
              name: "contacts[0].wa_id",
              customName: "wa_contact_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. CRM consent logging HubSpot
      hubspotPostNode(
        "7",
        "Log WhatsApp consent in HubSpot CRM",
        1480,
        `/crm/v3/objects/contacts/{normalized_email}?idProperty=email`,
        [
          {
            key: "properties",
            value:
              '{"whatsapp_optin":"true","whatsapp_phone":"{user_phone}","whatsapp_consent_date":"{optin_timestamp}","whatsapp_consent_text":"{consent_text}","whatsapp_wa_id":"{wa_contact_id}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1262. WHATSAPP WELCOME CONFIRMATION (7 nodes)
  // ===================================================================

  "WhatsApp Welcome Confirmation": {
    summary:
      "Receives WhatsApp message webhook, AI-extracts phone data, validates phone number via runCode, branches on validation result, sends WhatsApp welcome message via API, handles quick reply responses via runCode, and stores preferences in HubSpot CRM.",
    nodes: [
      trigger("Webhook: Incoming WhatsApp message", "webhook_app", {
        source: "whatsapp",
        event: "message.received",
        howItStarts:
          "Fires when a user sends their first WhatsApp message to the business number, indicating they want to start a conversation",
        setup:
          "Configure WhatsApp Business API webhook to POST incoming messages. Set up Meta Cloud API credentials. Configure HubSpot API key for preference storage.",
        inputFields: [],
      }),

      // 2. AI extract phone data
      aiNode(
        "2",
        "AI extract phone and message data",
        280,
        "Extract contact data from this raw WhatsApp webhook payload. Find the sender phone number, display name, message text, message type, and timestamp. Return JSON: {phone, display_name, message_text, message_type, timestamp}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "phone", as: "sender_phone", type: "string" },
          { field: "display_name", as: "sender_name", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "message_type", as: "message_type", type: "string" },
          { field: "timestamp", as: "message_timestamp", type: "string" },
        ],
      ),

      // 3. Phone number validation runCode
      n("3", "runCode", "Validate phone number format", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and normalize phone number for WhatsApp API
const phone = (vars.sender_phone || "").replace(/[^0-9+]/g, "");
const issues = [];
if (phone.length < 10) issues.push("Phone too short");
if (phone.length > 15) issues.push("Phone too long");
// Check for known test/invalid numbers
if (phone.startsWith("0000") || phone === "1234567890") issues.push("Test/invalid number detected");
const wa_phone = phone.startsWith("+") ? phone.substring(1) : phone;
const phone_valid = issues.length === 0 ? "yes" : "no";
const validation_notes = issues.length > 0 ? issues.join("; ") : "Valid phone number";
return { wa_phone, phone_valid, validation_notes };`,
          outputVars: [
            { name: "wa_phone", type: "string" },
            { name: "phone_valid", type: "string" },
            { name: "validation_notes", type: "string" },
          ],
        },
      }),

      // 4. Validation branch condition
      n("4", "condition", "Check if phone number is valid", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{phone_valid}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 5. WhatsApp welcome message API (True — valid phone)
      n("5", "hitApi", "Send WhatsApp welcome template message", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{whatsapp_phone_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "messaging_product", value: "whatsapp" },
            { key: "to", value: "{wa_phone}" },
            { key: "type", value: "interactive" },
            {
              key: "interactive",
              value:
                '{"type":"button","body":{"text":"Welcome to ' +
                "{company_name}" +
                ", " +
                "{sender_name}" +
                '! How can we help you today?"},"action":{"buttons":[{"type":"reply","reply":{"id":"browse_products","title":"Browse Products"}},{"type":"reply","reply":{"id":"track_order","title":"Track Order"}},{"type":"reply","reply":{"id":"get_support","title":"Get Support"}}]}}',
            },
          ],
          variables: [
            {
              name: "messages[0].id",
              customName: "wa_welcome_msg_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Quick reply handler runCode
      n("6", "runCode", "Process quick reply selection for routing", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map quick reply selection to preference and routing
const messageText = (vars.message_text || "").toLowerCase();
let preference_category = "general";
let routing_queue = "general_support";
let welcome_response = "Thanks for reaching out!";
if (messageText.includes("browse") || messageText.includes("product")) {
  preference_category = "shopping";
  routing_queue = "sales";
  welcome_response = "Great choice! Let me show you our latest products.";
} else if (messageText.includes("track") || messageText.includes("order")) {
  preference_category = "order_tracking";
  routing_queue = "fulfillment";
  welcome_response = "Sure! Please share your order number and I will look it up.";
} else if (messageText.includes("support") || messageText.includes("help")) {
  preference_category = "support";
  routing_queue = "customer_support";
  welcome_response = "Of course! A support agent will be with you shortly.";
}
return { preference_category, routing_queue, welcome_response };`,
          outputVars: [
            { name: "preference_category", type: "string" },
            { name: "routing_queue", type: "string" },
            { name: "welcome_response", type: "string" },
          ],
        },
      }),

      // 7. CRM preference storage HubSpot
      hubspotPostNode(
        "7",
        "Store WhatsApp preferences in HubSpot",
        1480,
        `/crm/v3/objects/contacts`,
        [
          {
            key: "properties",
            value:
              '{"phone":"{sender_phone}","firstname":"{sender_name}","whatsapp_active":"true","whatsapp_preference":"{preference_category}","whatsapp_first_message":"{message_timestamp}","whatsapp_routing_queue":"{routing_queue}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1263. WINNER AD CLONE PIPELINE (10 nodes)
  // ===================================================================

  "Winner Ad Clone Pipeline": {
    summary:
      "Runs on schedule to extract winning ad data from Meta Ads, AI-analyzes performance, AI-generates variant copy, generates creative assets via AI, gates through human review, creates ad campaign via Meta API, sets up performance monitoring via runCode, waits 7 days, and reports results to Sheets.",
    nodes: [
      trigger("Schedule: Weekly winning ad identification", "schedule", {
        source: "meta_ads",
        event: "performance.review",
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs weekly to identify top-performing ads in Meta Ads account, extract winning creative elements, and generate optimized variants for scaling",
        setup:
          "Configure Meta Marketing API access token with ads_read and ads_management permissions. Set up OpenAI API key for creative generation. Configure Google Sheets for reporting.",
        inputFields: [],
      }),

      // 2. Meta Ads winning ad data extraction
      n("2", "hitApi", "Fetch top-performing ads from Meta Ads API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/act_{ad_account_id}/insights?fields=ad_id,ad_name,impressions,clicks,spend,actions,cost_per_action_type,ctr,cpc&sort=actions_desc&level=ad&limit=5&date_preset=last_7d",
          headers: [{ key: "Authorization", value: "Bearer {meta_ads_token}" }],
          body: [],
          variables: [
            {
              name: "data[0].ad_id",
              customName: "winner_ad_id",
              type: "string",
            },
            {
              name: "data[0].ad_name",
              customName: "winner_ad_name",
              type: "string",
            },
            {
              name: "data[0].impressions",
              customName: "winner_impressions",
              type: "string",
            },
            {
              name: "data[0].clicks",
              customName: "winner_clicks",
              type: "string",
            },
            {
              name: "data[0].spend",
              customName: "winner_spend",
              type: "string",
            },
            { name: "data[0].ctr", customName: "winner_ctr", type: "string" },
            { name: "data[0].cpc", customName: "winner_cpc", type: "string" },
            {
              name: "data[0].actions[0].value",
              customName: "winner_conversions",
              type: "string",
            },
            {
              name: "data[0].cost_per_action_type[0].value",
              customName: "winner_cpa",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI performance analysis
      aiNode(
        "3",
        "AI analyze winning ad performance patterns",
        520,
        "You are a paid media analyst. Analyze this winning ad performance data and identify the key success factors. Determine what makes this ad outperform others — is it the copy angle, audience targeting, creative format, or offer structure? Return JSON: {success_factors, recommended_angles, target_audience_insights, copy_tone, offer_structure, scaling_strategy}",
        "Winning Ad: {winner_ad_name}\\nImpressions: {winner_impressions}\\nClicks: {winner_clicks}\\nCTR: {winner_ctr}%\\nCPC: ${winner_cpc}\\nConversions: {winner_conversions}\\nCPA: ${winner_cpa}\\nSpend: ${winner_spend}\\n\\nAnalyze performance patterns and recommend scaling strategy.",
        [
          { field: "success_factors", as: "success_factors", type: "string" },
          {
            field: "recommended_angles",
            as: "recommended_angles",
            type: "string",
          },
          {
            field: "target_audience_insights",
            as: "audience_insights",
            type: "string",
          },
          { field: "copy_tone", as: "copy_tone", type: "string" },
          { field: "offer_structure", as: "offer_structure", type: "string" },
          { field: "scaling_strategy", as: "scaling_strategy", type: "string" },
        ],
      ),

      // 4. AI variant copy generation
      aiNode(
        "4",
        "AI generate 3 ad copy variants",
        760,
        "You are a performance ad copywriter. Based on the winning ad analysis, generate 3 distinct copy variants that maintain the winning elements while testing new angles. Each variant should have a headline (max 40 chars), primary text (max 125 chars), and description (max 30 chars). Return JSON: {variant_1_headline, variant_1_primary, variant_1_description, variant_2_headline, variant_2_primary, variant_2_description, variant_3_headline, variant_3_primary, variant_3_description}",
        "Success Factors: {success_factors}\\nCopy Tone: {copy_tone}\\nOffer Structure: {offer_structure}\\nRecommended Angles: {recommended_angles}\\nTarget Audience: {audience_insights}\\n\\nGenerate 3 ad copy variants.",
        [
          { field: "variant_1_headline", as: "v1_headline", type: "string" },
          { field: "variant_1_primary", as: "v1_primary", type: "string" },
          {
            field: "variant_1_description",
            as: "v1_description",
            type: "string",
          },
          { field: "variant_2_headline", as: "v2_headline", type: "string" },
          { field: "variant_2_primary", as: "v2_primary", type: "string" },
          {
            field: "variant_2_description",
            as: "v2_description",
            type: "string",
          },
          { field: "variant_3_headline", as: "v3_headline", type: "string" },
          { field: "variant_3_primary", as: "v3_primary", type: "string" },
          {
            field: "variant_3_description",
            as: "v3_description",
            type: "string",
          },
        ],
      ),

      // 5. Creative asset generation AI
      aiNode(
        "5",
        "AI generate creative brief for ad assets",
        1000,
        "You are a creative director for paid social ads. Based on the ad copy variants and winning ad analysis, generate a detailed creative brief for the design team. Include visual direction, color palette, layout suggestions, and image/video recommendations. Return JSON: {creative_brief, visual_direction, color_palette, layout_type, asset_format, recommended_dimensions}",
        "Variant 1: {v1_headline} / {v1_primary}\\nVariant 2: {v2_headline} / {v2_primary}\\nVariant 3: {v3_headline} / {v3_primary}\\nSuccess Factors: {success_factors}\\nAudience: {audience_insights}\\n\\nCreate a creative brief for asset production.",
        [
          { field: "creative_brief", as: "creative_brief", type: "string" },
          { field: "visual_direction", as: "visual_direction", type: "string" },
          { field: "color_palette", as: "color_palette", type: "string" },
          { field: "layout_type", as: "layout_type", type: "string" },
          { field: "asset_format", as: "asset_format", type: "string" },
          {
            field: "recommended_dimensions",
            as: "asset_dimensions",
            type: "string",
          },
        ],
      ),

      // 6. Human review approval gate
      n("6", "approvalGate", "Human review ad variants before launch", 1240, {
        approvalGateDetails: {
          approverEmail: "{media_buyer_email}",
          approvalMessage:
            "Winner Ad Clone Pipeline — Review Required\\n\\nOriginal Winner: {winner_ad_name}\\nCTR: {winner_ctr}% | CPC: ${winner_cpc} | CPA: ${winner_cpa}\\n\\nSuccess Factors: {success_factors}\\nScaling Strategy: {scaling_strategy}\\n\\nVariant 1: {v1_headline} — {v1_primary}\\nVariant 2: {v2_headline} — {v2_primary}\\nVariant 3: {v3_headline} — {v3_primary}\\n\\nCreative Brief: {creative_brief}\\nVisual Direction: {visual_direction}\\nFormat: {asset_format} ({asset_dimensions})\\n\\nApprove to create campaign with these variants.",
        },
      }),

      // 7. Ad campaign creation Meta API
      n("7", "hitApi", "Create new ad campaign in Meta Ads", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_ads_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Clone_{winner_ad_name}_{_timestamp}" },
            { key: "objective", value: "OUTCOME_SALES" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
          ],
          variables: [
            { name: "id", customName: "new_campaign_id", type: "string" },
          ],
        },
      }),

      // 8. Performance monitoring setup runCode
      n("8", "runCode", "Set up performance monitoring thresholds", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate performance thresholds based on winning ad metrics
const winnerCTR = parseFloat(vars.winner_ctr) || 1.0;
const winnerCPC = parseFloat(vars.winner_cpc) || 1.0;
const winnerCPA = parseFloat(vars.winner_cpa) || 10.0;
// Set alert thresholds at 80% of winner performance
const ctr_threshold = (winnerCTR * 0.8).toFixed(2);
const cpc_threshold = (winnerCPC * 1.25).toFixed(2);
const cpa_threshold = (winnerCPA * 1.25).toFixed(2);
// Set daily budget at 50% of winner daily spend
const winnerSpend = parseFloat(vars.winner_spend) || 100;
const daily_budget = Math.round((winnerSpend / 7) * 0.5 * 100) / 100;
const monitoring_notes = "Monitor for 7 days. Pause variants below CTR " + ctr_threshold + "% or above CPA $" + cpa_threshold;
return { ctr_threshold, cpc_threshold, cpa_threshold, daily_budget, monitoring_notes };`,
          outputVars: [
            { name: "ctr_threshold", type: "string" },
            { name: "cpc_threshold", type: "string" },
            { name: "cpa_threshold", type: "string" },
            { name: "daily_budget", type: "number" },
            { name: "monitoring_notes", type: "string" },
          ],
        },
      }),

      // 9. Delay 7 days
      n("9", "delayAction", "Wait 7 days for performance data", 1960, {
        delayDetails: { duration: "7", unit: "days" },
      }),

      // 10. Results reporting Sheets
      sheetsNode(
        "10",
        "Log campaign results to Google Sheets",
        2200,
        "AdCloneResults",
        '[["{winner_ad_name}","{new_campaign_id}","{v1_headline}","{v2_headline}","{v3_headline}","{winner_ctr}","{winner_cpc}","{winner_cpa}","${daily_budget}","{ctr_threshold}","{cpa_threshold}","{monitoring_notes}","{_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 1264. WINNER AD REFRESH CYCLE (8 nodes)
  // ===================================================================

  "Winner Ad Refresh Cycle": {
    summary:
      "Runs on schedule to analyze Meta Ads performance trends, extracts creative elements via runCode, AI-generates refresh variant copy, AI-creates refreshed asset briefs, sends staging preview to Slack, gates through human approval, and executes automated ad swap via Meta API.",
    nodes: [
      trigger("Schedule: Bi-weekly ad fatigue check", "schedule", {
        source: "meta_ads",
        event: "performance.fatigue_check",
        schedule: "Every other Monday at 9:00 AM",
        howItStarts:
          "Runs bi-weekly to detect ad fatigue signals in Meta Ads campaigns, identify ads with declining performance, and generate refreshed creative variants",
        setup:
          "Configure Meta Marketing API access token. Set up OpenAI API key for creative refresh. Configure Slack webhook for preview notifications.",
        inputFields: [],
      }),

      // 2. Meta Ads performance trend analysis
      n("2", "hitApi", "Fetch ad performance trends from Meta Ads", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/act_{ad_account_id}/insights?fields=ad_id,ad_name,impressions,clicks,spend,frequency,actions,ctr,cpc,cpp&level=ad&date_preset=last_14d&sort=frequency_desc&limit=10",
          headers: [{ key: "Authorization", value: "Bearer {meta_ads_token}" }],
          body: [],
          variables: [
            {
              name: "data[0].ad_id",
              customName: "fatigued_ad_id",
              type: "string",
            },
            {
              name: "data[0].ad_name",
              customName: "fatigued_ad_name",
              type: "string",
            },
            {
              name: "data[0].frequency",
              customName: "ad_frequency",
              type: "string",
            },
            { name: "data[0].ctr", customName: "current_ctr", type: "string" },
            { name: "data[0].cpc", customName: "current_cpc", type: "string" },
            {
              name: "data[0].impressions",
              customName: "total_impressions",
              type: "string",
            },
            {
              name: "data[0].spend",
              customName: "total_spend",
              type: "string",
            },
            {
              name: "data[0].actions[0].value",
              customName: "total_conversions",
              type: "string",
            },
          ],
        },
      }),

      // 3. Creative element extraction runCode
      n(
        "3",
        "runCode",
        "Extract creative elements and detect fatigue signals",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Analyze fatigue signals and extract refresh priorities
const frequency = parseFloat(vars.ad_frequency) || 1;
const ctr = parseFloat(vars.current_ctr) || 0;
const cpc = parseFloat(vars.current_cpc) || 0;
let fatigue_level = "low";
let refresh_priority = "monitor";
let fatigue_signals = [];
if (frequency > 4) { fatigue_signals.push("High frequency (" + frequency.toFixed(1) + "x)"); fatigue_level = "high"; refresh_priority = "urgent"; }
else if (frequency > 2.5) { fatigue_signals.push("Moderate frequency (" + frequency.toFixed(1) + "x)"); fatigue_level = "medium"; refresh_priority = "recommended"; }
if (ctr < 0.8) { fatigue_signals.push("Low CTR (" + ctr + "%)"); }
if (cpc > 3.0) { fatigue_signals.push("High CPC ($" + cpc + ")"); }
const fatigue_summary = fatigue_signals.length > 0 ? fatigue_signals.join("; ") : "No significant fatigue detected";
const refresh_elements = frequency > 3 ? "headline,visual,cta" : frequency > 2 ? "headline,cta" : "headline";
return { fatigue_level, refresh_priority, fatigue_summary, refresh_elements };`,
            outputVars: [
              { name: "fatigue_level", type: "string" },
              { name: "refresh_priority", type: "string" },
              { name: "fatigue_summary", type: "string" },
              { name: "refresh_elements", type: "string" },
            ],
          },
        },
      ),

      // 4. AI refresh variant generation
      aiNode(
        "4",
        "AI generate refreshed ad copy variants",
        760,
        "You are a performance marketing creative strategist specializing in ad refresh. The current ad is showing fatigue signals. Generate refreshed copy that maintains the core value proposition but with fresh angles, new hooks, and updated CTAs. The refresh should feel new without losing the winning elements. Return JSON: {refresh_headline, refresh_primary_text, refresh_cta, refresh_angle, differentiation_notes}",
        "Fatigued Ad: {fatigued_ad_name}\\nFatigue Level: {fatigue_level}\\nSignals: {fatigue_summary}\\nFrequency: {ad_frequency}x\\nCurrent CTR: {current_ctr}%\\nElements to Refresh: {refresh_elements}\\n\\nGenerate refreshed ad copy.",
        [
          { field: "refresh_headline", as: "refresh_headline", type: "string" },
          {
            field: "refresh_primary_text",
            as: "refresh_primary",
            type: "string",
          },
          { field: "refresh_cta", as: "refresh_cta", type: "string" },
          { field: "refresh_angle", as: "refresh_angle", type: "string" },
          { field: "differentiation_notes", as: "diff_notes", type: "string" },
        ],
      ),

      // 5. Asset refresh AI
      aiNode(
        "5",
        "AI generate refreshed creative brief",
        1000,
        "You are a creative director for paid social. Create a refreshed creative brief for the design team that evolves the current visual while maintaining brand consistency. Focus on fresh visual hooks, updated imagery, and new layout variations. Return JSON: {visual_refresh_notes, new_imagery_direction, layout_variation, color_accent_shift, motion_suggestion}",
        "Original Ad: {fatigued_ad_name}\\nRefresh Headline: {refresh_headline}\\nRefresh Primary: {refresh_primary}\\nRefresh Angle: {refresh_angle}\\nFatigue Level: {fatigue_level}\\n\\nCreate a refreshed creative brief.",
        [
          {
            field: "visual_refresh_notes",
            as: "visual_refresh",
            type: "string",
          },
          {
            field: "new_imagery_direction",
            as: "imagery_direction",
            type: "string",
          },
          { field: "layout_variation", as: "layout_variation", type: "string" },
          { field: "color_accent_shift", as: "color_shift", type: "string" },
          {
            field: "motion_suggestion",
            as: "motion_suggestion",
            type: "string",
          },
        ],
      ),

      // 6. Staging preview Slack
      slackNode(
        "6",
        "Send ad refresh preview to Slack for review",
        1240,
        "Ad Refresh Preview — {fatigue_level} Fatigue\\n\\nOriginal: {fatigued_ad_name}\\nFrequency: {ad_frequency}x | CTR: {current_ctr}% | CPC: ${current_cpc}\\nFatigue: {fatigue_summary}\\n\\nRefreshed Copy:\\nHeadline: {refresh_headline}\\nPrimary: {refresh_primary}\\nCTA: {refresh_cta}\\nAngle: {refresh_angle}\\n\\nVisual: {visual_refresh}\\nImagery: {imagery_direction}\\nLayout: {layout_variation}\\n\\nDiff Notes: {diff_notes}",
      ),

      // 7. Human approval gate
      n("7", "approvalGate", "Approve refreshed ad before swapping", 1480, {
        approvalGateDetails: {
          approverEmail: "{media_buyer_email}",
          approvalMessage:
            "Ad Refresh Ready for Swap\\n\\nFatigued Ad: {fatigued_ad_name} (ID: {fatigued_ad_id})\\nFatigue Level: {fatigue_level} | Priority: {refresh_priority}\\n\\nRefreshed Copy:\\n- Headline: {refresh_headline}\\n- Primary: {refresh_primary}\\n- CTA: {refresh_cta}\\n\\nCreative Direction:\\n- {visual_refresh}\\n- {imagery_direction}\\n\\nApprove to pause the fatigued ad and launch the refreshed variant.",
        },
      }),

      // 8. Automated swap execution Meta API
      n("8", "hitApi", "Pause fatigued ad and update creative in Meta", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{fatigued_ad_id}",
          headers: [
            { key: "Authorization", value: "Bearer {meta_ads_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "status", value: "PAUSED" },
            { key: "name", value: "REFRESHED_{fatigued_ad_name}_{_timestamp}" },
          ],
          variables: [
            { name: "id", customName: "paused_ad_id", type: "string" },
            { name: "success", customName: "pause_success", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1265. WINNER CONTENT REPURPOSING (9 nodes)
  // ===================================================================

  "Winner Content Repurposing": {
    summary:
      "Runs on schedule to identify top content from Sheets, extracts content via runCode, AI-adapts content for multiple platforms, generates visual asset briefs via AI, checks brand voice compliance, publishes to multiple platforms via Buffer API, includes in email newsletter, and logs cross-platform performance to Sheets.",
    nodes: [
      trigger("Schedule: Weekly top content identification", "schedule", {
        source: "content_analytics",
        event: "content.top_performers",
        schedule: "Every Friday at 10:00 AM",
        howItStarts:
          "Runs weekly to identify the best-performing content pieces from the past week and repurpose them across multiple platforms for maximum reach",
        setup:
          "Configure Google Sheets with content performance data. Set up Buffer API for multi-platform publishing. Configure OpenAI API key for content adaptation.",
        inputFields: [],
      }),

      // 2. Top content identification Sheets
      n("2", "hitApi", "Fetch top performing content from Google Sheets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ContentPerformance!A2:H10?majorDimension=ROWS",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values[0][0]",
              customName: "top_content_title",
              type: "string",
            },
            {
              name: "values[0][1]",
              customName: "top_content_url",
              type: "string",
            },
            {
              name: "values[0][2]",
              customName: "top_content_type",
              type: "string",
            },
            {
              name: "values[0][3]",
              customName: "top_engagement_score",
              type: "string",
            },
            {
              name: "values[0][4]",
              customName: "top_content_category",
              type: "string",
            },
            {
              name: "values[0][5]",
              customName: "top_content_author",
              type: "string",
            },
            {
              name: "values[0][6]",
              customName: "top_content_date",
              type: "string",
            },
          ],
        },
      }),

      // 3. Content extraction runCode
      n("3", "runCode", "Extract key content elements for repurposing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and prepare content for multi-platform adaptation
const title = vars.top_content_title || "Untitled";
const contentType = (vars.top_content_type || "article").toLowerCase();
const score = parseFloat(vars.top_engagement_score) || 0;
const category = vars.top_content_category || "general";
// Determine repurpose formats based on content type
let platforms = ["twitter", "linkedin", "instagram"];
let content_formats = [];
if (contentType === "article" || contentType === "blog") {
  content_formats = ["twitter_thread", "linkedin_post", "instagram_carousel", "email_snippet"];
} else if (contentType === "video") {
  content_formats = ["twitter_clip", "linkedin_native", "instagram_reel", "email_embed"];
} else {
  content_formats = ["twitter_post", "linkedin_share", "instagram_story", "email_feature"];
}
const repurpose_count = content_formats.length;
const is_high_performer = score > 75 ? "yes" : "no";
return { platforms: platforms.join(","), content_formats: content_formats.join(","), repurpose_count, is_high_performer, original_type: contentType };`,
          outputVars: [
            { name: "platforms", type: "string" },
            { name: "content_formats", type: "string" },
            { name: "repurpose_count", type: "number" },
            { name: "is_high_performer", type: "string" },
            { name: "original_type", type: "string" },
          ],
        },
      }),

      // 4. AI platform-specific adaptation
      aiNode(
        "4",
        "AI adapt content for each platform",
        760,
        "You are a social media content strategist. Adapt this top-performing content for multiple platforms. Each platform version should feel native, not copy-pasted. Twitter: concise with hook. LinkedIn: professional with insights. Instagram: visual-first with engaging caption. Return JSON: {twitter_text, linkedin_text, instagram_caption, email_snippet, hashtags, key_takeaway}",
        "Title: {top_content_title}\\nURL: {top_content_url}\\nType: {original_type}\\nCategory: {top_content_category}\\nAuthor: {top_content_author}\\nEngagement Score: {top_engagement_score}\\nFormats: {content_formats}\\n\\nAdapt this content for Twitter, LinkedIn, Instagram, and email.",
        [
          { field: "twitter_text", as: "twitter_text", type: "string" },
          { field: "linkedin_text", as: "linkedin_text", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          { field: "email_snippet", as: "email_snippet", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
          { field: "key_takeaway", as: "key_takeaway", type: "string" },
        ],
      ),

      // 5. Visual asset generation AI
      aiNode(
        "5",
        "AI generate visual asset briefs per platform",
        1000,
        "You are a visual content designer. Create platform-specific visual asset briefs for repurposing this content. Each brief should include dimensions, style, key visual elements, and text overlay recommendations. Return JSON: {twitter_visual_brief, linkedin_visual_brief, instagram_visual_brief, overall_color_theme, brand_elements}",
        "Content: {top_content_title}\\nCategory: {top_content_category}\\nKey Takeaway: {key_takeaway}\\nTwitter: {twitter_text}\\nLinkedIn: {linkedin_text}\\nInstagram: {instagram_caption}\\n\\nCreate visual briefs for each platform.",
        [
          {
            field: "twitter_visual_brief",
            as: "twitter_visual",
            type: "string",
          },
          {
            field: "linkedin_visual_brief",
            as: "linkedin_visual",
            type: "string",
          },
          {
            field: "instagram_visual_brief",
            as: "instagram_visual",
            type: "string",
          },
          { field: "overall_color_theme", as: "color_theme", type: "string" },
          { field: "brand_elements", as: "brand_elements", type: "string" },
        ],
      ),

      // 6. Brand voice compliance condition
      n(
        "6",
        "condition",
        "Check if content meets high-performer threshold",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{is_high_performer}",
                operator: "equals",
                value: "yes",
              },
            ],
          },
        },
      ),

      // 7. Multi-platform publishing Buffer API (True — high performer)
      n("7", "hitApi", "Publish to social platforms via Buffer API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.bufferapp.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "text", value: "{twitter_text} {hashtags}" },
            { key: "profile_ids[]", value: "{buffer_twitter_profile_id}" },
            { key: "shorten", value: "true" },
            { key: "now", value: "false" },
          ],
          variables: [
            {
              name: "updates[0].id",
              customName: "buffer_update_id",
              type: "string",
            },
            { name: "success", customName: "buffer_success", type: "boolean" },
          ],
        },
      }),

      // 8. Email newsletter inclusion
      emailNode(
        "8",
        "Send content spotlight newsletter",
        1720,
        "{newsletter_list_email}",
        "This week's top content: {top_content_title}",
        "This Week's Top Content Spotlight\\n\\n{top_content_title}\\nBy {top_content_author}\\n\\n{email_snippet}\\n\\nKey Takeaway: {key_takeaway}\\n\\nRead the Full Piece: {top_content_url}?utm_source=newsletter&utm_campaign=content_repurpose\\n\\nFollow us for more:\\n- Twitter: @{company_name}\\n- LinkedIn: /company/{company_name}\\n- Instagram: @{company_name}\\n\\n{hashtags}",
      ),

      // 9. Cross-platform performance Sheets
      sheetsNode(
        "9",
        "Log repurposed content to performance tracker",
        1960,
        "ContentRepurpose",
        '[["{top_content_title}","{top_content_url}","{original_type}","{top_engagement_score}","{platforms}","{content_formats}","{buffer_update_id}","published","{_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1266. WISHLIST REMINDER FOR LOYALTY MEMBERS (8 nodes)
  // ===================================================================

  "Wishlist Reminder for Loyalty Members": {
    summary:
      "Runs on schedule to fetch wishlist and loyalty data from HubSpot, calculates points-to-discount conversion via runCode, detects price drops with a condition, generates AI-personalized email, branches on stock alerts, generates points redemption deep links via runCode, and logs conversions to Sheets.",
    nodes: [
      trigger("Schedule: Daily wishlist price check", "schedule", {
        source: "loyalty_system",
        event: "wishlist.price_check",
        schedule: "Daily at 7:00 AM",
        howItStarts:
          "Runs daily to check loyalty member wishlists for price drops, calculate available points discounts, and send personalized reminder emails",
        setup:
          "Configure HubSpot API for loyalty and wishlist data. Set up Shopify API for price monitoring. Configure Google Sheets for conversion tracking. Set up OpenAI API key for personalized emails.",
        inputFields: [],
      }),

      // 2. HubSpot wishlist and loyalty data fetch
      hubspotGetNode(
        "2",
        "Fetch loyalty member wishlist data from HubSpot",
        280,
        `crm/v3/objects/contacts?properties=email,firstname,loyalty_points,loyalty_tier,wishlist_items,wishlist_last_updated&filterGroups=[{"filters":[{"propertyName":"loyalty_member","operator":"EQ","value":"true"},{"propertyName":"wishlist_items","operator":"HAS_PROPERTY"}]}]&limit=50`,
        [
          {
            name: "results[0].properties.email",
            customName: "member_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "member_name",
            type: "string",
          },
          {
            name: "results[0].properties.loyalty_points",
            customName: "loyalty_points",
            type: "string",
          },
          {
            name: "results[0].properties.loyalty_tier",
            customName: "loyalty_tier",
            type: "string",
          },
          {
            name: "results[0].properties.wishlist_items",
            customName: "wishlist_items",
            type: "string",
          },
          {
            name: "results[0].properties.wishlist_last_updated",
            customName: "wishlist_updated",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 3. Points-to-discount calculation runCode
      n("3", "runCode", "Calculate loyalty points discount value", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate points value and available discount
const points = parseInt(vars.loyalty_points) || 0;
const tier = (vars.loyalty_tier || "bronze").toLowerCase();
// Points-to-dollar conversion by tier
const conversionRates = { bronze: 0.01, silver: 0.012, gold: 0.015, platinum: 0.02 };
const rate = conversionRates[tier] || 0.01;
const points_value = (points * rate).toFixed(2);
const max_discount_percent = tier === "platinum" ? 30 : tier === "gold" ? 25 : tier === "silver" ? 20 : 15;
// Parse wishlist items (stored as JSON string)
let wishlist_count = 0;
try {
  const items = JSON.parse(vars.wishlist_items || "[]");
  wishlist_count = items.length;
} catch (e) {
  wishlist_count = (vars.wishlist_items || "").split(",").length;
}
const has_price_drop = "yes"; // Will be confirmed by condition node
const points_available = points > 0 ? "yes" : "no";
return { points_value, max_discount_percent, wishlist_count, has_price_drop, points_available, conversion_rate: rate };`,
          outputVars: [
            { name: "points_value", type: "string" },
            { name: "max_discount_percent", type: "number" },
            { name: "wishlist_count", type: "number" },
            { name: "has_price_drop", type: "string" },
            { name: "points_available", type: "string" },
            { name: "conversion_rate", type: "number" },
          ],
        },
      }),

      // 4. Price drop detection condition
      n("4", "condition", "Check if member has redeemable points", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{points_available}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 5. AI personalized email generation (True — has points)
      aiNode(
        "5",
        "AI generate personalized wishlist reminder",
        1000,
        "You are a loyalty program email specialist. Write a personalized wishlist reminder email for a loyalty member. Emphasize the points discount they can apply, create urgency around price drops, and make it easy to act. Tone should match their tier level (platinum = exclusive/VIP, gold = valued, silver/bronze = encouraging). Return JSON: {subject_line, email_body, urgency_note, tier_perk_highlight}",
        "Member: {member_name}\\nTier: {loyalty_tier}\\nPoints: {loyalty_points} (worth ${points_value})\\nMax Discount: {max_discount_percent}%\\nWishlist Items: {wishlist_count} items\\nLast Updated: {wishlist_updated}\\n\\nWrite a personalized wishlist reminder email.",
        [
          { field: "subject_line", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "urgency_note", as: "urgency_note", type: "string" },
          { field: "tier_perk_highlight", as: "tier_perk", type: "string" },
        ],
      ),

      // 6. Stock alert branch condition
      n("6", "condition", "Check if wishlist has more than 3 items", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{wishlist_count}",
              operator: "greater_than",
              value: "3",
            },
          ],
        },
      }),

      // 7. Points redemption deep link runCode
      n("7", "runCode", "Generate points redemption deep links", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate deep links for wishlist + points redemption
const email = encodeURIComponent(vars.member_email || "");
const points = vars.loyalty_points || "0";
const pointsValue = vars.points_value || "0";
const wishlistUrl = "https://" + (vars.store_domain || "store.com") + "/account/wishlist?apply_points=true&points=" + points + "&utm_source=wishlist_reminder&utm_campaign=loyalty_points";
const redeemUrl = "https://" + (vars.store_domain || "store.com") + "/account/loyalty/redeem?email=" + email + "&auto_apply=true&utm_source=wishlist_reminder";
const tierPerksUrl = "https://" + (vars.store_domain || "store.com") + "/loyalty/" + (vars.loyalty_tier || "bronze").toLowerCase() + "-perks?utm_source=wishlist_reminder";
return { wishlist_url: wishlistUrl, redeem_url: redeemUrl, tier_perks_url: tierPerksUrl };`,
          outputVars: [
            { name: "wishlist_url", type: "string" },
            { name: "redeem_url", type: "string" },
            { name: "tier_perks_url", type: "string" },
          ],
        },
      }),

      // 7b. Email send for wishlist reminder
      emailNode(
        "7b",
        "Send wishlist reminder with points redemption",
        1720,
        "{member_email}",
        "{email_subject}",
        "{email_body}\\n\\n{urgency_note}\\n\\nYour Points Value: ${points_value} ({loyalty_points} points)\\nTier Perk: {tier_perk}\\n\\nView Your Wishlist: {wishlist_url}\\nRedeem Points: {redeem_url}\\nYour {loyalty_tier} Perks: {tier_perks_url}\\n\\nBest,\\nThe {company_name} Team",
      ),

      // 8. Conversion tracking Sheets
      sheetsNode(
        "8",
        "Log wishlist reminder to conversion tracker",
        1960,
        "WishlistReminders",
        '[["{member_email}","{loyalty_tier}","{loyalty_points}","${points_value}","{wishlist_count}","{email_subject}","sent","{_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "7b"),
      e("7b", "8"),
    ],
  },

  // ===================================================================
  // 1267. ZERO-CONVERSION CLEANUP (8 nodes)
  // ===================================================================

  "Zero-Conversion Cleanup": {
    summary:
      "Runs on schedule to extract Google Ads campaign data, identifies zero-conversion campaigns via runCode, runs AI diagnostic analysis, branches on action recommendation, sends review report to Slack, auto-pauses high-waste campaigns via Google Ads API, and logs budget reallocation to Sheets.",
    nodes: [
      trigger("Schedule: Daily zero-conversion audit", "schedule", {
        source: "google_ads",
        event: "campaign.audit",
        schedule: "Daily at 6:00 AM",
        howItStarts:
          "Runs daily to audit all active Google Ads campaigns, identify those with zero conversions and significant spend, diagnose potential issues, and auto-pause wasteful campaigns",
        setup:
          "Configure Google Ads API access with campaign management permissions. Set up OpenAI API key for diagnostic analysis. Configure Slack webhook for team notifications. Set up Google Sheets for budget tracking.",
        inputFields: [],
      }),

      // 2. Google Ads campaign data extraction
      n(
        "2",
        "hitApi",
        "Fetch active campaign performance from Google Ads",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "developer-token", value: "{google_ads_dev_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  "SELECT campaign.id, campaign.name, campaign.status, metrics.cost_micros, metrics.impressions, metrics.clicks, metrics.conversions, metrics.cost_per_conversion, campaign.campaign_budget FROM campaign WHERE campaign.status = 'ENABLED' AND segments.date DURING LAST_7_DAYS ORDER BY metrics.cost_micros DESC LIMIT 50",
              },
            ],
            variables: [
              {
                name: "results[0].campaign.id",
                customName: "campaign_id",
                type: "string",
              },
              {
                name: "results[0].campaign.name",
                customName: "campaign_name",
                type: "string",
              },
              {
                name: "results[0].metrics.costMicros",
                customName: "cost_micros",
                type: "string",
              },
              {
                name: "results[0].metrics.impressions",
                customName: "impressions",
                type: "string",
              },
              {
                name: "results[0].metrics.clicks",
                customName: "clicks",
                type: "string",
              },
              {
                name: "results[0].metrics.conversions",
                customName: "conversions",
                type: "string",
              },
              {
                name: "results[0].metrics.costPerConversion",
                customName: "cost_per_conversion",
                type: "string",
              },
              {
                name: "results[0].campaign.campaignBudget",
                customName: "campaign_budget_resource",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Zero-conversion identification runCode
      n(
        "3",
        "runCode",
        "Identify zero-conversion campaigns and calculate waste",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Identify campaigns with zero conversions and calculate waste
const conversions = parseFloat(vars.conversions) || 0;
const costMicros = parseInt(vars.cost_micros) || 0;
const cost = costMicros / 1000000; // Convert micros to dollars
const impressions = parseInt(vars.impressions) || 0;
const clicks = parseInt(vars.clicks) || 0;
const ctr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : "0";
const is_zero_conversion = conversions === 0 && cost > 10 ? "yes" : "no";
const waste_amount = conversions === 0 ? cost.toFixed(2) : "0";
let waste_severity = "none";
if (cost > 500 && conversions === 0) waste_severity = "critical";
else if (cost > 100 && conversions === 0) waste_severity = "high";
else if (cost > 25 && conversions === 0) waste_severity = "medium";
else if (cost > 10 && conversions === 0) waste_severity = "low";
const daily_burn = (cost / 7).toFixed(2);
const projected_monthly_waste = conversions === 0 ? (parseFloat(daily_burn) * 30).toFixed(2) : "0";
return { is_zero_conversion, waste_amount, waste_severity, daily_burn, projected_monthly_waste, actual_cost: cost.toFixed(2), calculated_ctr: ctr };`,
            outputVars: [
              { name: "is_zero_conversion", type: "string" },
              { name: "waste_amount", type: "string" },
              { name: "waste_severity", type: "string" },
              { name: "daily_burn", type: "string" },
              { name: "projected_monthly_waste", type: "string" },
              { name: "actual_cost", type: "string" },
              { name: "calculated_ctr", type: "string" },
            ],
          },
        },
      ),

      // 4. AI diagnostic analysis
      aiNode(
        "4",
        "AI diagnose zero-conversion campaign issues",
        760,
        "You are a Google Ads optimization expert. Diagnose why this campaign has zero conversions despite spending money. Analyze the metrics to identify probable issues: bad targeting, poor landing page, wrong bid strategy, low CTR, mismatched keywords, or budget issues. Provide specific actionable recommendations. Return JSON: {diagnosis, primary_issue, secondary_issues, recommended_action, should_pause, recovery_steps, estimated_savings}",
        "Campaign: {campaign_name} (ID: {campaign_id})\\nSpend: ${actual_cost} over 7 days\\nImpressions: {impressions}\\nClicks: {clicks}\\nCTR: {calculated_ctr}%\\nConversions: {conversions}\\nWaste Severity: {waste_severity}\\nDaily Burn: ${daily_burn}\\n\\nDiagnose why this campaign has zero conversions.",
        [
          { field: "diagnosis", as: "diagnosis", type: "string" },
          { field: "primary_issue", as: "primary_issue", type: "string" },
          { field: "secondary_issues", as: "secondary_issues", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "should_pause", as: "should_pause", type: "string" },
          { field: "recovery_steps", as: "recovery_steps", type: "string" },
          {
            field: "estimated_savings",
            as: "estimated_savings",
            type: "string",
          },
        ],
      ),

      // 5. Action recommendation condition
      n("5", "condition", "Check if AI recommends pausing the campaign", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{should_pause}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 6. Review report Slack
      slackNode(
        "6",
        "Send zero-conversion diagnostic report to Slack",
        1240,
        "Zero-Conversion Campaign Alert ({waste_severity})\\n\\nCampaign: {campaign_name}\\nSpend: ${actual_cost} | Daily Burn: ${daily_burn}/day\\nProjected Monthly Waste: ${projected_monthly_waste}\\nImpressions: {impressions} | Clicks: {clicks} | CTR: {calculated_ctr}%\\n\\nDiagnosis: {diagnosis}\\nPrimary Issue: {primary_issue}\\nSecondary: {secondary_issues}\\n\\nRecommended Action: {recommended_action}\\nRecovery Steps: {recovery_steps}\\nEst. Savings: {estimated_savings}\\n\\nAuto-Pause: {should_pause}",
      ),

      // 7. Auto-pause high-waste Google Ads API (True — should pause)
      n(
        "7",
        "hitApi",
        "Auto-pause zero-conversion campaign in Google Ads",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/campaigns:mutate",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "developer-token", value: "{google_ads_dev_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "operations",
                value:
                  '[{"update":{"resourceName":"customers/{google_ads_customer_id}/campaigns/{campaign_id}","status":"PAUSED"},"updateMask":"status"}]',
              },
            ],
            variables: [
              {
                name: "results[0].resourceName",
                customName: "paused_campaign_resource",
                type: "string",
              },
            ],
          },
        },
      ),

      // 8. Budget reallocation log Sheets
      sheetsNode(
        "8",
        "Log budget reallocation to Sheets",
        1720,
        "ZeroConversionCleanup",
        '[["{campaign_name}","{campaign_id}","${actual_cost}","${daily_burn}","${projected_monthly_waste}","{waste_severity}","{primary_issue}","{recommended_action}","{should_pause}","{estimated_savings}","{_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
